import * as React from 'react';

import type {DynamicLabels} from 'src/types/dynamic-labels';
import type {
  AudienceRule,
  ActionTypeAudienceRule,
  AudienceRuleApi,
  AudienceBlocks,
  AudienceBlocksApi,
  AudienceListTableEntry,
  AudienceListStatus,
  AssociatedResource,
  AudienceListApiResponse,
  NewAudienceListSidePanels,
  EditAudienceListSidePanels,
  AssociatedResources,
  CombinedActionAudienceRule,
  StatusOption,
  AudienceListSource,
  VariableEntry,
  PaginationData,
  SortData,
  ScopeOption,
  CriteriaValue,
  ABT_ATTRIBUTE_KEY_SET_TYPE,
  EntityTypeAudienceRule,
} from 'src/types/audience-list';
import type {ApiWorkflow, Workflow} from 'src/types/workflow';
import type {
  EntityMetadataObject,
  EntityMetadataEntityAttributes,
} from 'src/types/ats-entities';

import uniqueId from 'lodash/uniqueId';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import some from 'lodash/some';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {hashObject, sortObject} from 'src/utils/index';
import type {SenseApiEvent} from 'src/types/events';
import {checkForString} from 'src/components/workflow/event/content/module/editors/beefree';
import {
  surveyLinkEmailToken,
  surveyContentToken,
} from 'src/components/workflow/event/constants';
import {
  isNewActionTypeRule,
  normalizeActionRule as normalizeNewActionRule,
} from 'src/utils/audience-revamp';


export const PAGE_SIZE = 14;

const commonAudienceListSidePanels = ['details', 'rules', 'review'];

export const newAudienceListSidePanels: NewAudienceListSidePanels[] = [
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'NewAudienceListSidePanels'.
  ...commonAudienceListSidePanels,
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'NewAudienceListSidePanels'.
  'copyLists',
];
// @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'EditAudienceListSidePanels[]'.
export const editAudienceListSidePanels: EditAudienceListSidePanels[] = [
  ...commonAudienceListSidePanels,
];

export const LIST_NAME_VALID_CHARS_REGEX: RegExp =
  /^[A-Za-z-\s\d\_\{\}\(\)\[\]\&\,\*\?\'\"\:\;\.]*$/;

export const isValidListName = (name: string): boolean =>
  LIST_NAME_VALID_CHARS_REGEX.test(name);

export const getListStatusOptions = (
  t: (key: string, defaultValue: string) => string,
): Array<StatusOption> => [
  {label: t('In_Use', 'In Use'), value: 'live'},
  {label: t('Ready_to_use', 'Ready to use'), value: 'published'},
  {label: t('Draft', 'Draft'), value: 'draft'},
  {label: t('Building', 'Building'), value: 'building'},
  {label: t('Archived', 'Archived'), value: 'archived'},
  {label: t('Failed', 'Failed'), value: 'failed'},
];

export function getListLabel(
  value: string,
  t: (key: string, defaultValue: string) => string,
): string {
  const statusOption: StatusOption | null | undefined = getListStatusOptions(
    t,
  ).find((option) => option.value === value);
  return statusOption ? statusOption.label : t('Unknown', 'Unknown');
}

const ABT_VALUES = new Set([
  'email_events_any_specific_email',
  'link_events_any_external_link',
  'survey_events_any_specific_survey',
  'survey_events_any_specific_survey_question',
  'link_events_any_specific_external_link',
]); //TODO(Vish):Get a list of all possible abt attribute values and list them here;

export const ARCHIVABLE_STATUSES = ['draft', 'published'] as const;

export const isListArchivable = (
  status: string,
  associatedResourcesCount: number,
): boolean => {
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"published" | "draft"'.
  return ARCHIVABLE_STATUSES.includes(status) && associatedResourcesCount === 0;
};

export const getListsToArchive = (
  selected: string[],
  lists: AudienceListTableEntry[],
): string[] =>
  selected.filter((id) => {
    const item = lists.find((list) => list.id === id);
    if (!item) {
      return false;
    }
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'. | TS18048 - 'item.metadata' is possibly 'undefined'.
    if (isListArchivable(item.status, item.metadata.associatedResourcesCount)) {
      return true;
    }
    return false;
  });

export const getListsToUnarchive = (
  selected: string[],
  lists: AudienceListTableEntry[],
): string[] =>
  selected.filter((id) => {
    const item = lists.find((list) => list.id === id);
    if (!item) {
      return false;
    }
    // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types 'AudienceListStatus | undefined' and '"archived"' have no overlap.
    return item.status === 'archived';
  });

export const getListsToPublish = (
  selected: string[],
  lists: AudienceListTableEntry[],
): string[] => {
  const listToPublish = selected.filter((id) => {
    const item = lists.find((list) => list.id === id);
    if (!item) {
      return false;
    }
    return item.status === 'draft';
  });

  return listToPublish;
};

export const DELETABLE_STATUSES = ['draft', 'published', 'archived'] as const;

export const isListDeletable = (
  everBeenLive: boolean,
  status: string,
  associatedResourcesCount: number,
): boolean => {
  return (
    everBeenLive === false &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"archived" | "published" | "draft"'.
    DELETABLE_STATUSES.includes(status) &&
    associatedResourcesCount === 0
  );
};

export const getListsToDelete = (
  selected: string[],
  lists: AudienceListTableEntry[],
): string[] =>
  selected.filter((id) => {
    const item = lists.find((list) => list.id === id);
    if (!item) {
      return false;
    }
    if (
      isListDeletable(
        // @ts-expect-error - TS2345 - Argument of type 'boolean | undefined' is not assignable to parameter of type 'boolean'.
        item.everBeenLive,
        item.status,
        // @ts-expect-error - TS18048 - 'item.metadata' is possibly 'undefined'.
        item.metadata.associatedResourcesCount,
      )
    ) {
      return true;
    }
    return false;
  });

export const getDepricatedOperatorAlertText = (
  t: (key: string, defaultValue: string) => string,
): string =>
  `${t(
    'The_operators_is_not_setpresent_is_empty_and_is_setpresent_are_deprecated_Please_use_the_is_emptynull_is_not_emptynotnull_operators_instead_and_save_the_rules_to_proceed',
    'The operators "is not set/present," "is empty," and "is set/present" are deprecated. Please use the "is empty/null," "is not empty/not-null" operators instead and save the rules to proceed',
  )}.`;

export const getOperatorOptions = (
  t: (key: string, defaultValue: string) => string,
): {
  [key: string]: Array<{
    value: string;
    label: string;
    tooltip?: string;
    secondaryLabel?: string;
    disabled?: boolean;
  }>;
} => {
  return {
    string: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('status', 'status')} = "${t('placed', 'placed')}"`,
      },
      {
        value: 'neq',
        label: t('is_not', 'is not'),
        tooltip: `${t('status', 'status')} ≠ "${t('placed', 'placed')}"`,
      },
      {
        value: 'empty',
        label: t('is_empty', 'is empty'),
        tooltip: t('status_is_empty', 'status is empty'),
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_null',
        label: `${t('is_not_set', 'is not set')}/${t('present', 'present')}`,
        tooltip: `${t('status', 'status')} = ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: `${t('is_set', 'is set')}/${t('present', 'present')}`,
        tooltip: `${t('status', 'status')} ≠ ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_not_emptynotnull_instead',
          'Use “is not empty/not-null” instead',
        ),
        disabled: true,
      },
      {
        value: 'contains',
        label: t('contains_one_of', 'contains one of'),
        tooltip: `${t(
          'email_contains_one_of',
          'email contains one of',
        )} "@gmail"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@gmail.in"`,
      },
      {
        value: 'ncontains',
        label: t('does_not_contain_one_of', 'does not contain one of'),
        tooltip: `${t('email', 'email')} ${t(
          'doesn\'t_contain_one_of',
          'doesn\'t contain one of',
        )} "@yahoo"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@outlook.in"`,
      },
      {
        value: 'starts_with',
        label: t('starts_with_one_of', 'starts with one of'),
        tooltip: `${t('name', 'name')} ${t(
          'starts_with_one_of',
          'starts with one of',
        )} "John"
      ${t('possible_values', 'possible values')} = "John R", "John Matthew"`,
      },
      {
        value: 'nstarts_with',
        label: t('does_not_start_with_one_of', 'does not start with one of'),
        tooltip: `${t('name', 'name')} ${t(
          'doesn\'t_start_with_one_of',
          'doesn\'t start with one of',
        )} "John"
      ${t('possible_values', 'possible values')} = "Mick K.", "Ram C"`,
      },
      {
        value: 'ends_with',
        label: t('ends_with_one_of', 'ends with one of'),
        tooltip: `${t('email', 'email')} ${t(
          'ends_with_one_of',
          'ends with one of',
        )} "@gmail.com", "@yahoo.com"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@yahoo.com"`,
      },
      {
        value: 'in',
        label: t('is_one_of', 'is one of'),
        tooltip: `${t('company_name', 'company name')} = "Dell" ${t(
          'OR',
          'OR',
        )} ${t('company_name', 'company name')} = "Microsoft"`,
      },
      {
        value: 'not_in',
        label: t('is_not_one_of', 'is not one of'),
        tooltip: `${t('company_name', 'company name')} ≠ "Dell" ${t(
          'AND',
          'AND',
        )} ${t('company_name', 'company name')} ≠ "Microsoft"`,
      },
      {
        value: 'is_empty_or_null',
        label: t('is_emptynull', 'is empty/null'),
        tooltip: `${t('status', 'status')} = ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_empty', 'is empty')}`,
      },
      {
        value: 'is_not_empty_and_not_null',
        label: t('is_not_empty_not_null', 'is not empty/ not null'),
        tooltip: `${t('status', 'status')} ≠ ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_not_empty', 'is not empty')}`,
      },
    ],
    number: [
      {
        value: 'gt',
        label: t('greater_than', 'greater than'),
        tooltip: `${t('value', 'value')} > 5`,
      },
      {
        value: 'lt',
        label: t('less_than', 'less than'),
        tooltip: `${t('value', 'value')} < 5`,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('value', 'value')} ${t('is_not_set', 'is not set')}`,
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('value', 'value')} ${t('is_set', 'is set')}`,
        disabled: true,
      },
      {
        value: 'eq',
        label: t('equals', 'equals'),
        tooltip: `${t('value', 'value')} = 5`,
      },
      {
        value: 'neq',
        label: t('not_equal_to', 'not equal to'),
        tooltip: `${t('value', 'value')} ≠ 5`,
      },
    ],
    currency: [
      {
        value: 'gt',
        label: t('greater_than', 'greater than'),
        tooltip: `${t('value', 'value')} > 5`,
      },
      {
        value: 'lt',
        label: t('less_than', 'less than'),
        tooltip: `${t('value', 'value')} < 5`,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('value', 'value')} ${t('is_not_set', 'is not set')}`,
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('value', 'value')} ${t('is_set', 'is set')}`,
        disabled: true,
      },
      {
        value: 'eq',
        label: t('equals', 'equals'),
        tooltip: `${t('value', 'value')} = 5`,
      },
      {
        value: 'neq',
        label: t('not_equal_to', 'not equal to'),
        tooltip: `${t('value', 'value')} ≠ 5`,
      },
    ],
    date: [
      {
        value: 'gt',
        label: t('is_after', 'is after'),
        tooltip: `${t('date_added', 'date_added')} > 2023-06-01 `,
      },
      {
        value: 'lt',
        label: t('is_before', 'is before'),
        tooltip: `${t('date_added', 'date_added')} < 2023-06-09`,
      },
      {
        value: 'eq',
        label: t('is_equal_to', 'is equal to'),
        tooltip: `${t('date_added', 'date_added')} = 2023-06-09 `,
      },
      {
        value: 'gte',
        label: t('is_on_or_after', 'is on or after'),
        tooltip: `${t('date_added', 'date_added')} >= 2023-06-01`,
      },
      {
        value: 'lte',
        label: t('is_on_or_before', 'is on or before'),
        tooltip: `${t('date_added', 'date_added')} ${t(
          'is_on_or_before',
          'is on or before',
        )} 2023-06-09`,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('date_added', 'date_added')} ${t(
          'is_not_set',
          'is not set',
        )}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('date_added', 'date_added')} ${t('is_set', 'is set')}`,
        secondaryLabel: t(
          'Use_is_not_emptynotnull_instead',
          'Use “is not empty/not-null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_empty_or_null',
        label: t('is_emptynull', 'is empty/null'),
        tooltip: `${t('status', 'status')} = ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_empty', 'is empty')}`,
      },
      {
        value: 'is_not_empty_and_not_null',
        label: t('is_not_empty_not_null', 'is not empty/ not null'),
        tooltip: `${t('status', 'status')} ≠ ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_not_empty', 'is not empty')}`,
      },
    ],

    boolean: [
      {
        // @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'string'.
        value: true,
        label: t('is_true', 'is true'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_true',
          'is true',
        )}`,
      },
      {
        // @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'string'.
        value: false,
        label: t('is_false', 'is false'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_false',
          'is false',
        )}`,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_not_set',
          'is not set',
        )}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_set',
          'is set',
        )}`,
        secondaryLabel: t(
          'Use_is_not_emptynotnull_instead',
          'Use “is not empty/not-null” instead',
        ),
        disabled: true,
      },
    ],
    email: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('status', 'status')} = "${t('placed', 'placed')}"`,
      },
      {
        value: 'neq',
        label: t('is_not', 'is not'),
        tooltip: `${t('status', 'status')} ≠ "${t('placed', 'placed')}"`,
      },
      {
        value: 'empty',
        label: t('is_empty', 'is empty'),
        tooltip: `${t('status', 'status')} ${t('is_empty', 'is empty')}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('status', 'status')} = ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('status', 'status')} ≠ ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_not_emptynotnull_instead',
          'Use “is not empty/not-null” instead',
        ),
        disabled: true,
      },
      {
        value: 'contains',
        label: t('contains_one_of', 'contains one of'),
        tooltip: `${t('email', 'email')} ${t(
          'contains_one_of',
          'contains one of',
        )} "@gmail"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@gmail.in"`,
      },
      {
        value: 'ncontains',
        label: t('does_not_contain_one_of', 'does not contain one of'),
        tooltip: `${t('email', 'email')} ${t(
          'doesn\'t_contain_one_of',
          'doesn\'t contain one of',
        )} "@yahoo"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@outlook.in"`,
      },
      {
        value: 'starts_with',
        label: t('starts_with_one_of', 'starts with one of'),
        tooltip: `${t('name', 'name')} ${t(
          'starts_with_one_of',
          'starts with one of',
        )} "John"
      ${t('possible_values', 'possible values')} = "John R", "John Matthew"`,
      },
      {
        value: 'nstarts_with',
        label: t('does_not_start_with_one_of', 'does not start with one of'),
        tooltip: `${t('name', 'name')} ${t(
          'doesn\'t_start_with_one_of',
          'doesn\'t start with one of',
        )} "John"
      ${t('possible_values', 'possible values')} = "Mick K.", "Ram C"`,
      },
      {
        value: 'ends_with',
        label: t('ends_with_one_of', 'ends with one of'),
        tooltip: `${t('email', 'email')} ${t(
          'ends_with_one_of',
          'ends with one of',
        )} "@gmail.com", "@yahoo.com"
      ${t(
        'possible_values',
        'possible values',
      )} = "abc@gmail.com", "xyz@yahoo.com"`,
      },
      {
        value: 'in',
        label: t('is_one_of', 'is one of'),
        tooltip: `${t('company_name', 'company name')} = "Dell" ${t(
          'OR',
          'OR',
        )} ${t('company_name', 'company name')} = "Microsoft"`,
      },
      {
        value: 'not_in',
        label: t('is_not_one_of', 'is not one of'),
        tooltip: `${t('company_name', 'company name')} ≠ "Dell" ${t(
          'AND',
          'AND',
        )} ${t('company_name', 'company name')} ≠ "Microsoft"`,
      },
      {
        value: 'is_empty_or_null',
        label: t('is_emptynull', 'is empty/null'),
        tooltip: `${t('status', 'status')} = ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_empty', 'is empty')}`,
      },
      {
        value: 'is_not_empty_and_not_null',
        label: t('is_not_emptynot_null', 'is not empty/not null'),
        tooltip: `${t('status', 'status')} ≠ ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('status', 'status')} ${t('is_not_empty', 'is not empty')}`,
      },
    ],
    phone: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('number', 'number')} = "+12123231324"`,
      },
      {
        value: 'neq',
        label: t('is_not', 'is not'),
        tooltip: `${t('number', 'number')} ≠ "+12123231324"`,
      },
      {
        value: 'empty',
        label: t('is_empty', 'is empty'),
        tooltip: `${t('number', 'number')} ${t('is_empty', 'is empty')}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_null',
        label: t('is_not_setpresent', 'is not set/present'),
        tooltip: `${t('number', 'number')} = ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_emptynull_instead',
          'Use “is empty/null” instead',
        ),
        disabled: true,
      },
      {
        value: 'is_not_null',
        label: t('is_setpresent', 'is set/present'),
        tooltip: `${t('number', 'number')} ≠ ${t('NULL', 'NULL')}`,
        secondaryLabel: t(
          'Use_is_not_emptynotnull_instead',
          'Use “is not empty/not-null” instead',
        ),
        disabled: true,
      },
      {
        value: 'contains',
        label: t('contains_one_of', 'contains one of'),
        tooltip: `${t('number', 'number')} ${t(
          'contains_one_of',
          'contains one of',
        )} "1"
        ${t('possible_values', 'possible values')} = "91435", "12914356"`,
      },
      {
        value: 'ncontains',
        label: t('does_not_contain_one_of', 'does not contain one of'),
        tooltip: `${t('number', 'number')} ${t(
          'doesn\'t_contain_one_of',
          'doesn\'t contain one of',
        )} "1"
        ${t('possible_values', 'possible values')} = "92435", "2924356"`,
      },
      {
        value: 'starts_with',
        label: t('starts_with_one_of', 'starts with one of'),
        tooltip: `${t('number', 'number')} ${t(
          'starts_with_one_of',
          'starts with one of',
        )} "123"
        ${t('possible_values', 'possible values')} = "12393828", "123590"`,
      },
      {
        value: 'nstarts_with',
        label: t('does_not_start_with_one_of', 'does not start with one of'),
        tooltip: `${t('number', 'number')} ${t(
          'doesn\'t_start_with_one_of',
          'doesn\'t start with one of',
        )} "123"
        ${t('possible_values', 'possible values')} = "12938", "148590"`,
      },
      {
        value: 'ends_with',
        label: t('ends_with_one_of', 'ends with one of'),
        tooltip: `${t('number', 'number')} ${t(
          'ends_with_one_of',
          'ends with one of',
        )} "90"
        ${t('possible_values', 'possible values')} = "1238090", "379890"`,
      },
      {
        value: 'in',
        label: t('is_one_of', 'is one of'),
        tooltip: `${t('number', 'number')} = "123" ${t('or', 'or')} ${t(
          'number',
          'number',
        )} = "456"`,
      },
      {
        value: 'not_in',
        label: t('is_not_one_of', 'is not one of'),
        tooltip: `${t('number', 'number')} ≠ "123" ${t('or', 'or')} ${t(
          'number',
          'number',
        )} ≠ "456"`,
      },
      {
        value: 'is_empty_or_null',
        label: t('is_emptynull', 'is empty/null'),
        tooltip: `${t('number', 'number')} = ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('number', 'number')} ${t('is_empty', 'is empty')}`,
      },
      {
        value: 'is_not_empty_and_not_null',
        label: t('is_not_empty_not_null', 'is not empty/ not null'),
        tooltip: `${t('number', 'number')} ≠ ${t('NULL', 'NULL')} ${t(
          'or',
          'or',
        )} ${t('number', 'number')} ${t('is_not_empty', 'is not empty')}`,
      },
    ],
  };
};

export const DEFAULT_OPERATOR_OPTION = {
  string: 'eq',
  number: 'gt',
  date: 'gt',
  boolean: true,
  email: 'eq',
  phone: 'eq',
} as const;

export const MAX_LIMIT_IN_NOT_IN = 50000;
export const MAX_LIMIT_SUBSTRING = 100;

//Todo(vish): Get all possible abt supported survey questions from backend.
export const ABT_SUPPORTED_SURVEY_QUESTIONS = [
  'nps_survey_question',
  'multiple_choice_survey_question',
  'text_survey_question',
  'calendar_date_survey_question',
  'rating_scale_survey_question',
] as const;

export const SCOPE_OPTIONS: ScopeOption[] = [
  {
    label: 'Custom Date Range',
    value: 'custom_date_range',
    tooltip: 'You have a fixed date range from X to Y',
  },
  {
    label: 'Specific Date to Till now',
    value: 'specific_date_to_now',
    tooltip: 'You have a fixed "from" Date to capture actions, till now',
  },
];

export const getScopeOptions = (
  t: (key: string, defaultValue: string) => string,
): ScopeOption[] => [
  {
    label: t('Custom_Date_Range', 'Custom Date Range'),
    value: 'custom_date_range',
    tooltip: t(
      'You_have_a_fixed_date_range_from_X_to_Y',
      'You have a fixed date range from X to Y',
    ),
  },
  {
    label: t('Specific_Date_to_Till_Now', 'Specific Date to Till Now'),
    value: 'specific_date_to_now',
    tooltip: t(
      'You_have_a_fixed_from_Date_to_capture_actions_till_now',
      'You have a fixed "from" Date to capture actions, till now',
    ),
  },
];

//TODO(vish): Get all possible abt attribute keys from backend.
export const ABT_ATTRIBUTE_KEY_SET = {
  email_events_any_specific_email: {
    initialRuleItems: {
      criteriaField: {
        criteriaLabel: null,
        criteriaValue: null,
        journeyLabel: null,
        journeyValue: null,
        touchpointLabel: null,
        touchpointValue: null,
      },
      scopeField: {
        scopeLabel: null,
        scopeValue: null,
        fromDate: null,
        toDate: null,
      },
    },
    primaryBoxTitle: 'Select Email',
    criteriaValues: [
      {label: 'Email has been opened', value: 'email_opened'},
      {label: 'Email has been sent', value: 'email_sent'},
      {label: 'Email has been clicked', value: 'email_clicked'},
      {label: 'Email has bounced', value: 'email_bounced'},
      {label: 'Email has been delivered', value: 'email_delivered'},
    ],
    secondaryBoxTitle: 'Select Scope',
    scopeValues: SCOPE_OPTIONS,
    dslCreatorHelper: [
      {
        attribute_name: ['fromRule', 'criteriaValue', 'field'],
        from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
        value: ['fixed', true],
      },
      {
        attribute_name: ['fixed', 'reference_type', 'field'],
        value: ['fixed', 'journey'],
      },
      {
        attribute_name: ['fixed', 'reference_id', 'field'],
        value: ['fromRule', 'journeyValue'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_type', 'field'],
        value: ['fixed', 'touchpoint'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_id', 'field'],
        value: ['fromRule', 'touchpointValue'],
      },
      {
        operator: ['fixed', 'gte'],
        attribute_name: ['fixed', 'start_date', 'field'],
        value: ['fromRule', 'fromDate'],
      },
      {
        operator: ['fixed', 'lte'],
        attribute_name: ['fixed', 'end_date', 'field'],
        value: ['fromRule', 'toDate'],
      },
    ],
    criteriaList: [
      'email_opened',
      'email_sent',
      'email_clicked',
      'email_bounced',
      'email_delivered',
    ],
    supportedTouchpointTypes: {
      send_type: ['email'],
      event_type: [] as Array<string>,
    },
    //Todo(vish): (Temporary) These required keys will be removed once we remove labels from initial rule items.
    requiredCriteriaKeys: ['criteriaValue', 'journeyValue', 'touchpointValue'],
    requiredScopeKeys: ['fromDate', 'toDate'],
  },
  survey_events_any_specific_survey: {
    initialRuleItems: {
      criteriaField: {
        criteriaLabel: null,
        criteriaValue: null,
        journeyLabel: null,
        journeyValue: null,
        touchpointLabel: null,
        touchpointValue: null,
      },
      scopeField: {
        scopeLabel: null,
        scopeValue: null,
        fromDate: null,
        toDate: null,
      },
    },
    primaryBoxTitle: 'Select Criteria and Survey',
    criteriaValues: [
      {label: 'Survey Completed', value: 'survey_completed'},
      {label: 'Survey Not Started', value: 'survey_not_started'},
    ],
    secondaryBoxTitle: 'Select Scope',
    scopeValues: SCOPE_OPTIONS,
    dslCreatorHelper: [
      {
        attribute_name: ['fromRule', 'criteriaValue', 'field'],
        from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
        value: ['fixed', true],
      },
      {
        attribute_name: ['fixed', 'reference_type', 'field'],
        value: ['fixed', 'journey'],
      },
      {
        attribute_name: ['fixed', 'reference_id', 'field'],
        value: ['fromRule', 'journeyValue'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_type', 'field'],
        value: ['fixed', 'touchpoint'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_id', 'field'],
        value: ['fromRule', 'touchpointValue'],
      },
      {
        operator: ['fixed', 'gte'],
        attribute_name: ['fixed', 'start_date', 'field'],
        value: ['fromRule', 'fromDate'],
      },
      {
        operator: ['fixed', 'lte'],
        attribute_name: ['fixed', 'end_date', 'field'],
        value: ['fromRule', 'toDate'],
      },
    ],
    criteriaList: ['survey_completed', 'survey_not_started'],
    supportedTouchpointTypes: {
      send_type: [] as Array<string>,
      event_type: ['survey', 'sms_survey', 'beefree_email'],
    },
    requiredCriteriaKeys: ['criteriaValue', 'journeyValue', 'touchpointValue'],
    requiredScopeKeys: ['fromDate', 'toDate'],
  },
  survey_events_any_specific_survey_question: {
    initialRuleItems: {
      criteriaField: {
        journeyLabel: null,
        journeyValue: null,
        touchpointLabel: null,
        touchpointValue: null,
        questionLabel: null,
        questionValue: null,
      },
      scopeField: {
        scopeLabel: null,
        scopeValue: null,
        fromDate: null,
        toDate: null,
        multipleChoices: null,
        npsOperator: null,
        npsValue: null,
        calendarDateOperator: null,
        calendarDateValue: null,
        ratingScaleOperator: null,
        ratingScaleValue: null,
        keywords: null,
      },
    },
    primaryBoxTitle: 'Select Survey',
    secondaryBoxTitle: 'Select criteria and scope',
    scopeValues: SCOPE_OPTIONS,
    criteriaValues: [] as Array<CriteriaValue>,
    dslCreatorHelper: [
      {
        attribute_name: ['fixed', 'survey_question_response', 'field'],
        from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
        value: ['fixed', true],
      },
      {
        attribute_name: ['fixed', 'reference_type', 'field'],
        value: ['fixed', 'touchpoint'],
      },
      {
        attribute_name: ['fixed', 'reference_id', 'field'],
        value: ['fromRule', 'touchpointValue'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_type', 'field'],
        value: ['fixed', 'event_module'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_id', 'field'],
        value: ['fromRule', 'questionValue'],
      },
      {
        operator: ['fromRule', 'npsOperator'],
        attribute_name: ['fixed', 'survey_nps_score', 'field'],
        value: ['fromRule', 'npsValue'],
      },
      {
        operator: ['fromRule', 'calendarDateOperator'],
        attribute_name: ['fixed', 'survey_calendar_date', 'field'],
        value: ['fromRule', 'calendarDateValue'],
      },
      {
        operator: ['fromRule', 'ratingScaleOperator'],
        attribute_name: ['fixed', 'survey_rating_scale', 'field'],
        value: ['fromRule', 'ratingScaleValue'],
      },
      {
        operator: ['fixed', 'contains'],
        attribute_name: ['fixed', 'survey_text', 'field'],
        value: ['fromRule', 'keywords'],
      },
      {
        operator: ['fixed', 'in'],
        attribute_name: ['fixed', 'survey_multiple_options', 'field'],
        value: ['fromRule', 'multipleChoices'],
      },
      {
        attribute_name: ['fixed', 'action_journey_id', 'field'],
        value: ['fromRule', 'journeyValue'],
      },
      {
        operator: ['fixed', 'gte'],
        attribute_name: ['fixed', 'start_date', 'field'],
        value: ['fromRule', 'fromDate'],
      },
      {
        operator: ['fixed', 'lte'],
        attribute_name: ['fixed', 'end_date', 'field'],
        value: ['fromRule', 'toDate'],
      },
    ],
    criteriaList: ['survey_question_response'],
    supportedTouchpointTypes: {
      send_type: [] as Array<string>,
      event_type: ['survey', 'sms_survey', 'beefree_email'],
    },
    requiredCriteriaKeys: ['questionValue', 'journeyValue', 'touchpointValue'],
    requiredScopeKeys: ['fromDate', 'toDate'],
  },
  link_events_any_specific_external_link: {
    initialRuleItems: {
      criteriaField: {
        criteriaLabel: null,
        criteriaValue: null,
        journeyLabel: null,
        journeyValue: null,
        touchpointLabel: null,
        touchpointValue: null,
        linkValue: null,
      },
      scopeField: {
        scopeLabel: null,
        scopeValue: null,
        fromDate: null,
        toDate: null,
      },
    },
    primaryBoxTitle: 'Select link',
    criteriaValues: [
      {label: 'External link has been sent', value: 'external_link_sent'},
      {label: 'External Link has been clicked', value: 'external_link_clicked'},
    ],
    secondaryBoxTitle: 'Select Scope',
    scopeValues: SCOPE_OPTIONS,
    dslCreatorHelper: [
      {
        attribute_name: ['fromRule', 'criteriaValue', 'field'],
        from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
        value: ['fixed', true],
      },
      {
        attribute_name: ['fixed', 'reference_type', 'field'],
        value: ['fixed', 'touchpoint'],
      },
      {
        attribute_name: ['fixed', 'reference_id', 'field'],
        value: ['fromRule', 'touchpointValue'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_type', 'field'],
        value: ['fixed', 'external_link'],
      },
      {
        attribute_name: ['fixed', 'sub_reference_id', 'field'],
        value: ['fromRule', 'linkValue'],
      },
      {
        attribute_name: ['fixed', 'action_journey_id', 'field'],
        value: ['fromRule', 'journeyValue'],
      },
      {
        operator: ['fixed', 'gte'],
        attribute_name: ['fixed', 'start_date', 'field'],
        value: ['fromRule', 'fromDate'],
      },
      {
        operator: ['fixed', 'lte'],
        attribute_name: ['fixed', 'end_date', 'field'],
        value: ['fromRule', 'toDate'],
      },
    ],
    criteriaList: ['external_link_sent', 'external_link_clicked'],
    supportedTouchpointTypes: {
      send_type: ['email'],
      event_type: [] as Array<string>,
    },
    requiredCriteriaKeys: [
      'criteriaValue',
      'journeyValue',
      'touchpointValue',
      'linkValue',
    ],
    requiredScopeKeys: ['fromDate', 'toDate'],
  },
} as const;

export const getABTAttributeKeySet = (
  t: (key: string, defaultValue: string) => string,
): ABT_ATTRIBUTE_KEY_SET_TYPE => {
  return {
    email_events_any_specific_email: {
      initialRuleItems: {
        criteriaField: {
          criteriaLabel: null,
          criteriaValue: null,
          journeyLabel: null,
          journeyValue: null,
          touchpointLabel: null,
          touchpointValue: null,
        },
        scopeField: {
          scopeLabel: null,
          scopeValue: null,
          fromDate: null,
          toDate: null,
        },
      },
      primaryBoxTitle: t('Select_Email', 'Select Email'),
      criteriaValues: [
        {
          label: t('Email_has_been_opened', 'Email has been opened'),
          value: 'email_opened',
        },
        {
          label: t('Email_has_been_sent', 'Email has been sent'),
          value: 'email_sent',
        },
        {
          label: t('Email_has_been_clicked', 'Email has been clicked'),
          value: 'email_clicked',
        },
        {
          label: t('Email_has_bounced', 'Email has bounced'),
          value: 'email_bounced',
        },
        {
          label: t('Email_has_been_delivered', 'Email has been delivered'),
          value: 'email_delivered',
        },
      ],
      secondaryBoxTitle: t('Select_Scope', 'Select Scope'),
      scopeValues: getScopeOptions(t),
      dslCreatorHelper: [
        {
          attribute_name: ['fromRule', 'criteriaValue', 'field'],
          from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
          value: ['fixed', true],
        },
        {
          attribute_name: ['fixed', 'reference_type', 'field'],
          value: ['fixed', 'journey'],
        },
        {
          attribute_name: ['fixed', 'reference_id', 'field'],
          value: ['fromRule', 'journeyValue'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_type', 'field'],
          value: ['fixed', 'touchpoint'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_id', 'field'],
          value: ['fromRule', 'touchpointValue'],
        },
        {
          operator: ['fixed', 'gte'],
          attribute_name: ['fixed', 'start_date', 'field'],
          value: ['fromRule', 'fromDate'],
        },
        {
          operator: ['fixed', 'lte'],
          attribute_name: ['fixed', 'end_date', 'field'],
          value: ['fromRule', 'toDate'],
        },
      ],
      criteriaList: [
        'email_opened',
        'email_sent',
        'email_clicked',
        'email_bounced',
        'email_delivered',
      ],
      supportedTouchpointTypes: {
        send_type: ['email'],
        event_type: [] as Array<string>,
      },
      //Todo(vish): (Temporary) These required keys will be removed once we remove labels from initial rule items.
      requiredCriteriaKeys: [
        'criteriaValue',
        'journeyValue',
        'touchpointValue',
      ],
      requiredScopeKeys: ['fromDate', 'toDate'],
    },
    survey_events_any_specific_survey: {
      initialRuleItems: {
        criteriaField: {
          criteriaLabel: null,
          criteriaValue: null,
          journeyLabel: null,
          journeyValue: null,
          touchpointLabel: null,
          touchpointValue: null,
        },
        scopeField: {
          scopeLabel: null,
          scopeValue: null,
          fromDate: null,
          toDate: null,
        },
      },
      primaryBoxTitle: t(
        'Select_Criteria_and_Survey',
        'Select Criteria and Survey',
      ),
      criteriaValues: [
        {
          label: t('Survey_Completed', 'Survey Completed'),
          value: 'survey_completed',
        },
        {
          label: t('Survey_Not_Started', 'Survey Not Started'),
          value: 'survey_not_started',
        },
      ],
      secondaryBoxTitle: t('Select_Scope', 'Select Scope'),
      scopeValues: getScopeOptions(t),
      dslCreatorHelper: [
        {
          attribute_name: ['fromRule', 'criteriaValue', 'field'],
          from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
          value: ['fixed', true],
        },
        {
          attribute_name: ['fixed', 'reference_type', 'field'],
          value: ['fixed', 'journey'],
        },
        {
          attribute_name: ['fixed', 'reference_id', 'field'],
          value: ['fromRule', 'journeyValue'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_type', 'field'],
          value: ['fixed', 'touchpoint'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_id', 'field'],
          value: ['fromRule', 'touchpointValue'],
        },
        {
          operator: ['fixed', 'gte'],
          attribute_name: ['fixed', 'start_date', 'field'],
          value: ['fromRule', 'fromDate'],
        },
        {
          operator: ['fixed', 'lte'],
          attribute_name: ['fixed', 'end_date', 'field'],
          value: ['fromRule', 'toDate'],
        },
      ],
      criteriaList: ['survey_completed', 'survey_not_started'],
      supportedTouchpointTypes: {
        send_type: [] as Array<string>,
        event_type: ['survey', 'sms_survey', 'beefree_email'],
      },
      requiredCriteriaKeys: [
        'criteriaValue',
        'journeyValue',
        'touchpointValue',
      ],
      requiredScopeKeys: ['fromDate', 'toDate'],
    },
    survey_events_any_specific_survey_question: {
      initialRuleItems: {
        criteriaField: {
          journeyLabel: null,
          journeyValue: null,
          touchpointLabel: null,
          touchpointValue: null,
          questionLabel: null,
          questionValue: null,
        },
        scopeField: {
          scopeLabel: null,
          scopeValue: null,
          fromDate: null,
          toDate: null,
          multipleChoices: null,
          npsOperator: null,
          npsValue: null,
          calendarDateOperator: null,
          calendarDateValue: null,
          ratingScaleOperator: null,
          ratingScaleValue: null,
          keywords: null,
        },
      },
      primaryBoxTitle: t('Select_Survey', 'Select Survey'),
      secondaryBoxTitle: t(
        'Select_criteria_and_scope',
        'Select criteria and scope',
      ),
      scopeValues: getScopeOptions(t),
      criteriaValues: [] as Array<CriteriaValue>,
      dslCreatorHelper: [
        {
          attribute_name: ['fixed', 'survey_question_response', 'field'],
          from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
          value: ['fixed', true],
        },
        {
          attribute_name: ['fixed', 'reference_type', 'field'],
          value: ['fixed', 'touchpoint'],
        },
        {
          attribute_name: ['fixed', 'reference_id', 'field'],
          value: ['fromRule', 'touchpointValue'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_type', 'field'],
          value: ['fixed', 'event_module'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_id', 'field'],
          value: ['fromRule', 'questionValue'],
        },
        {
          operator: ['fromRule', 'npsOperator'],
          attribute_name: ['fixed', 'survey_nps_score', 'field'],
          value: ['fromRule', 'npsValue'],
        },
        {
          operator: ['fromRule', 'calendarDateOperator'],
          attribute_name: ['fixed', 'survey_calendar_date', 'field'],
          value: ['fromRule', 'calendarDateValue'],
        },
        {
          operator: ['fromRule', 'ratingScaleOperator'],
          attribute_name: ['fixed', 'survey_rating_scale', 'field'],
          value: ['fromRule', 'ratingScaleValue'],
        },
        {
          operator: ['fixed', 'contains'],
          attribute_name: ['fixed', 'survey_text', 'field'],
          value: ['fromRule', 'keywords'],
        },
        {
          operator: ['fixed', 'in'],
          attribute_name: ['fixed', 'survey_multiple_options', 'field'],
          value: ['fromRule', 'multipleChoices'],
        },
        {
          attribute_name: ['fixed', 'action_journey_id', 'field'],
          value: ['fromRule', 'journeyValue'],
        },
        {
          operator: ['fixed', 'gte'],
          attribute_name: ['fixed', 'start_date', 'field'],
          value: ['fromRule', 'fromDate'],
        },
        {
          operator: ['fixed', 'lte'],
          attribute_name: ['fixed', 'end_date', 'field'],
          value: ['fromRule', 'toDate'],
        },
      ],
      criteriaList: ['survey_question_response'],
      supportedTouchpointTypes: {
        send_type: [] as Array<string>,
        event_type: ['survey', 'sms_survey', 'beefree_email'],
      },
      requiredCriteriaKeys: [
        'questionValue',
        'journeyValue',
        'touchpointValue',
      ],
      requiredScopeKeys: ['fromDate', 'toDate'],
    },
    link_events_any_specific_external_link: {
      initialRuleItems: {
        criteriaField: {
          criteriaLabel: null,
          criteriaValue: null,
          journeyLabel: null,
          journeyValue: null,
          touchpointLabel: null,
          touchpointValue: null,
          linkValue: null,
        },
        scopeField: {
          scopeLabel: null,
          scopeValue: null,
          fromDate: null,
          toDate: null,
        },
      },
      primaryBoxTitle: t('Select_link', 'Select link'),
      criteriaValues: [
        {
          label: t(
            'External_link_has_been_sent',
            'External link has been sent',
          ),
          value: 'external_link_sent',
        },
        {
          label: t(
            'External_Link_has_been_clicked',
            'External Link has been clicked',
          ),
          value: 'external_link_clicked',
        },
      ],
      secondaryBoxTitle: t('Select_Scope', 'Select Scope'),
      scopeValues: getScopeOptions(t),
      dslCreatorHelper: [
        {
          attribute_name: ['fromRule', 'criteriaValue', 'field'],
          from_relations: ['fromRule', 'relatedEntitiesArray', 'field'],
          value: ['fixed', true],
        },
        {
          attribute_name: ['fixed', 'reference_type', 'field'],
          value: ['fixed', 'touchpoint'],
        },
        {
          attribute_name: ['fixed', 'reference_id', 'field'],
          value: ['fromRule', 'touchpointValue'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_type', 'field'],
          value: ['fixed', 'external_link'],
        },
        {
          attribute_name: ['fixed', 'sub_reference_id', 'field'],
          value: ['fromRule', 'linkValue'],
        },
        {
          attribute_name: ['fixed', 'action_journey_id', 'field'],
          value: ['fromRule', 'journeyValue'],
        },
        {
          operator: ['fixed', 'gte'],
          attribute_name: ['fixed', 'start_date', 'field'],
          value: ['fromRule', 'fromDate'],
        },
        {
          operator: ['fixed', 'lte'],
          attribute_name: ['fixed', 'end_date', 'field'],
          value: ['fromRule', 'toDate'],
        },
      ],
      criteriaList: ['external_link_sent', 'external_link_clicked'],
      supportedTouchpointTypes: {
        send_type: ['email'],
        event_type: [] as Array<string>,
      },
      requiredCriteriaKeys: [
        'criteriaValue',
        'journeyValue',
        'touchpointValue',
        'linkValue',
      ],
      requiredScopeKeys: ['fromDate', 'toDate'],
    },
  };
};

export const getFieldsToDesignations = (
  fields: DynamicLabels,
): Record<any, any> => {
  const fieldsToDesignations: Record<string, any> = {};
  fields.forEach(
    (field) => (fieldsToDesignations[field.value] = field.designations),
  );
  return Object.freeze(fieldsToDesignations);
};
export const getIsZipcodeField = (
  field: string,
  fields: DynamicLabels,
): boolean => {
  const fieldsToDesignations = getFieldsToDesignations(fields);
  //its a zipcode field if the designation contains zipcode
  return (fieldsToDesignations[field] ?? []).includes('ZIPCODE');
};
export const insertExtraZipcodeOperator = (
  operators: {
    label: string;
    value: string;
    tooltip?: string;
    disabled?: boolean;
    secondaryLabel?: string;
  }[],
  t: (arg1: string, arg2: string) => string,
): {
  label: string;
  value: string;
  tooltip?: string;
  disabled?: boolean;
  secondaryLabel?: string;
}[] =>
  operators.reduce<Array<any>>((acc, operator) => {
    if (operator.value === 'in') {
      return [
        ...acc,
        operator,
        //NOTE: there is no in_radius operator on backend. its just a ui construct.
        {
          label: t('is_within_radius_of', 'is within radius of'),
          value: 'in_radius',
          tooltip: `${t('radius', 'radius')} = 20 ${t(
            'miles_from_source_pincode',
            'miles from source pincode',
          )} (12069)`,
        },
      ];
    }
    return [...acc, operator];
  }, []);

export const IN_OPERATORS = ['in', 'not_in'] as const; // this will have 50k limit
export const SUBSTRING_OPERATORS = [
  // this will have 100 limit
  'contains',
  'ncontains',
  'starts_with',
  'nstarts_with',
  'ends_with',
] as const;

export const ARRAY_OPERATORS = [
  'in',
  'not_in',
  'contains',
  'ncontains',
  'starts_with',
  'nstarts_with',
  'ends_with',
] as const;
export const SINGLE_VALUE_OPERATORS = ['eq', 'neq', 'gt', 'lt'] as const;
export const ALLOWS_EMPTY_STRING_OPERATORS = ['in'] as const;
export const ALLOWS_EMPTY_DATE_OPERATORS = [
  'eq',
  'gt',
  'lt',
  'gte',
  'lte',
] as const;
export const VALUE_NOT_REQUIRED_OPERATORS = [
  'is_null',
  'is_not_null',
  'empty',
  'is_empty_or_null',
  'is_not_empty_and_not_null',
] as const;

export const DEPRECATED_OPERATORS = [
  'is_null',
  'is_not_null',
  'empty',
] as const;

export const getDateFilterOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
}> => [
  {
    value: 'today',
    label: t('Today', 'Today'),
  },
  {
    value: 'specific_date',
    label: t('Specific_Date', 'Specific Date'),
  },
  {
    value: 'relative_date',
    label: t('Relative_to', 'Relative to'),
  },
];

export const RELATIVE_DAYS_OPERATOR_OPTIONS = [
  {
    value: 'gt',
    label: 'is greater than',
  },
  {
    value: 'lt',
    label: 'is less than',
  },
] as const;

export const RELATIVE_DAYS_BEFORE_OR_AFTER = [
  {
    value: 'before',
    label: 'before',
  },
  {
    value: 'after',
    label: 'after',
  },
] as const;

export const getRelativeDaysMinusOrPlus = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
}> => [
  {
    value: 'minus',
    label: `${t('Minus', 'Minus')} (-)`,
  },
  {
    value: 'plus',
    label: `${t('Plus', 'Plus')} (+)`,
  },
];

export const isOperatorSwitchIncompatible = (
  newOperator: string,
  oldOperator?: string | boolean | null,
): boolean => {
  if (newOperator === oldOperator) {
    return false;
  }
  const nonArrayOperators = [
    ...SINGLE_VALUE_OPERATORS,
    ...VALUE_NOT_REQUIRED_OPERATORS,
  ];
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"empty" | "eq" | "neq" | "gt" | "lt" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
  const isNewOperatorNonArrayOperator = nonArrayOperators.includes(newOperator);
  // @ts-expect-error - TS2345 - Argument of type 'string | boolean | null | undefined' is not assignable to parameter of type '"empty" | "eq" | "neq" | "gt" | "lt" | "is_null" | "is_not_null" | "is_empty_or_null" | "is_not_empty_and_not_null"'.
  const isOldOperatorNonArrayOperator = nonArrayOperators.includes(oldOperator);

  return isNewOperatorNonArrayOperator !== isOldOperatorNonArrayOperator;
};

export const getLimitRelatedErrors = (
  allBlocks: Map<string, AudienceRule[]>,
  totalInNotInCount: number,
): Array<string> => {
  const isInNotInExceeding = totalInNotInCount > MAX_LIMIT_IN_NOT_IN;
  const ruleItemWithErrors: Array<string> = [];
  allBlocks.forEach((ruleBlockArr, ruleBlockKey) => {
    ruleBlockArr.forEach((ruleItem) => {
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'. | TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      if (ruleItem.operator && IN_OPERATORS.includes(ruleItem.operator)) {
        if (isInNotInExceeding) {
          ruleItemWithErrors.push(ruleItem.id);
        }
      } else if (
        // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
        ruleItem.operator &&
        // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
        SUBSTRING_OPERATORS.includes(ruleItem.operator)
      ) {
        // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
        const ruleValueCount = Array.isArray(ruleItem.value)
          ? // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
            ruleItem.value.length
          : 0;
        if (ruleValueCount > MAX_LIMIT_SUBSTRING) {
          ruleItemWithErrors.push(ruleItem.id);
        }
      }
    });
  });
  return ruleItemWithErrors;
};

export const isThereDeprecatedOperator = (
  allBlocks: Map<string, AudienceRule[]>,
): boolean => {
  for (const [ruleBlockKey, ruleBlockArr] of allBlocks) {
    if (
      ruleBlockArr.some(
        (ruleItem) =>
          // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'. | TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
          ruleItem.operator && DEPRECATED_OPERATORS.includes(ruleItem.operator),
      )
    ) {
      return true; // Found a deprecated operator, exit the function early
    }
  }
  return false; // No deprecated operators found
};

export const canEditListRules = (status: string): boolean =>
  !['building', 'archived'].includes(status);

export const isListEditable = (status: string): boolean =>
  !['archived'].includes(status);

export const isListDetailsEditable = (source: AudienceListSource): boolean =>
  !['ANALYTICS'].includes(source);

export const shouldShowABT = (source: AudienceListSource): boolean =>
  !['ANALYTICS'].includes(source);

export const ruleValueToStringRuleValue = (
  value: number | null | undefined | number[] | string | string[] | boolean,
): string[] => {
  if (!value) {
    return [];
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return value.map((v) => v.toString());
    }
    return [value.toString()];
  }
  return [];
};
export const ruleValueToNumberRuleValue = (
  value: number | null | undefined | number[] | string | string[] | boolean,
): string => {
  if (!value && value !== 0) {
    return '';
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return '';
    }
    return '' + value.toString();
  }
  return '';
};
export const ruleValueToDateRuleValue = (
  value: number | number[] | string | string[] | boolean | null | undefined,
  t: (arg1: string, arg2: string) => string,
): string => {
  if (!value) {
    return t('today', 'today');
  }
  if (value != null) {
    if (Array.isArray(value)) {
      return t('today', 'today');
    }
    return '' + value.toString();
  }
  return t('today', 'today');
};

export const getFieldsToTypes = (
  inclusions: Map<string, AudienceRule[]>,
  exclusions: Map<string, AudienceRule[]>,
  fields: DynamicLabels,
): {
  [key: string]: string;
} =>
  [...inclusions.values(), ...exclusions.values()]
    .flat()
    .reduce<Record<string, any>>((acc, rule) => {
      //TODO:(diwakersurya) optimize here if required
      // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
      if (rule.field) {
        const fieldObj = fields.find(
          // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
          (field) => field.value === rule.field.attribute_name,
        );
        const dataType = fieldObj?.type || 'string';
        // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
        acc[rule.field.attribute_name] = dataType;
      }
      return acc;
    }, {});

export const createInclusionBlockIdentifier = (): string =>
  uniqueId('inclusionBlock');

export const createExclusionBlockIdentifier = (): string =>
  uniqueId('exclusionBlock');

export const parseAudienceBlocks = (
  json: AudienceBlocksApi,
): AudienceBlocks => {
  const {all} = json;
  let {inclusions, exclusions} = json;
  inclusions = inclusions ?? {blocks: []};
  exclusions = exclusions ?? {blocks: []};

  let parsedInclusions = (inclusions.blocks ?? []).reduce((acc, block) => {
    const rules: Array<AudienceRule> = [];
    (block.rules ?? []).forEach((rule) => {
      if (isABTRuleFromDSL(rule.conditions)) {
        rules.push(parseActionRule(rule.conditions));
      } else {
        rules.push(parseAudienceRule(rule.conditions[0]));
      }
    });

    return acc.set(createInclusionBlockIdentifier(), rules);
  }, new Map());
  if (parsedInclusions.size === 0) {
    parsedInclusions = getDefaultInclusionBlock();
  }

  /**NOTE:(diwakersurya) all blocks from exclusion form only one block on the ui
   * by combining rules across all the blocks. this is done because exclusion
   * blocks have or between them and backend only supports or between blocks and
   * not rules.
   */
  const allExclusionRules = (exclusions.blocks ?? []).reduce<Array<any>>(
    (acc, block) => {
      const rules: Array<AudienceRule> = [];
      (block.rules ?? []).forEach((rule) => {
        if (isABTRuleFromDSL(rule.conditions)) {
          rules.push(parseActionRule(rule.conditions));
        } else {
          rules.push(parseAudienceRule(rule.conditions[0]));
        }
      });
      return [...acc, ...rules];
    },
    [],
  );
  let parsedExclusions = getDefaultExclusionBlock();
  if (allExclusionRules.length > 0) {
    parsedExclusions = new Map([
      [createExclusionBlockIdentifier(), allExclusionRules],
    ]);
  }

  return {all, inclusions: parsedInclusions, exclusions: parsedExclusions};
};

const getActionAttributeKey = (conditions: Array<AudienceRuleApi>) => {
  for (const {field} of conditions) {
    for (const key of Object.keys(ABT_ATTRIBUTE_KEY_SET)) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      const {criteriaList} = ABT_ATTRIBUTE_KEY_SET[key];
      if (criteriaList.includes(field.attribute_name)) {
        return {key, attributeName: field.attribute_name};
      }
    }
  }
  return {key: '', attributeName: ''};
};

const extractConditionOperator = (
  conditions: AudienceRuleApi[],
  attributeName: string,
): string | null | undefined =>
  conditions
    .find((condition) => condition.field.attribute_name === attributeName)
    ?.operator.toString();

const extractConditionValue = (
  conditions: AudienceRuleApi[],
  attributeName: string,
): any => {
  const node =
    conditions.find(
      (condition) => condition.field.attribute_name === attributeName,
    ) ?? {};
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type '{}'.
  if (node.value) {
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type '{}'. | TS2339 - Property 'value' does not exist on type '{}'. | TS2339 - Property 'value' does not exist on type '{}'.
    return Array.isArray(node.value) ? node.value : node.value.toString();
  }
  return null;
};

const getRelatedEntitiesForParsing = (
  conditions: AudienceRuleApi[],
): string[] =>
  conditions.find((condition) => condition.field.from_relations.length > 0)
    ?.field.from_relations ?? [];

export const parseActionRule = (
  conditions: AudienceRuleApi[],
): AudienceRule => {
  const {key: ABTAttributeKey, attributeName: criteriaValue} =
    getActionAttributeKey(conditions);
  const ruleItems = getCombinedRuleItems(ABTAttributeKey);
  switch (ABTAttributeKey) {
    case 'survey_events_any_specific_survey_question':
      ruleItems.journeyValue = extractConditionValue(
        conditions,
        'action_journey_id',
      );
      ruleItems.touchpointValue = extractConditionValue(
        conditions,
        'reference_id',
      );
      ruleItems.questionValue = extractConditionValue(
        conditions,
        'sub_reference_id',
      );
      ruleItems.multipleChoices = extractConditionValue(
        conditions,
        'survey_multiple_options',
      );
      ruleItems.keywords = extractConditionValue(conditions, 'survey_text');
      ruleItems.npsValue = extractConditionValue(
        conditions,
        'survey_nps_score',
      );
      ruleItems.npsOperator = extractConditionOperator(
        conditions,
        'survey_nps_score',
      );
      ruleItems.calendarDateValue = extractConditionValue(
        conditions,
        'survey_calendar_date',
      );
      ruleItems.calendarDateOperator = extractConditionOperator(
        conditions,
        'survey_calendar_date',
      );
      ruleItems.ratingScaleValue = extractConditionValue(
        conditions,
        'survey_rating_scale',
      );
      ruleItems.ratingScaleOperator = extractConditionOperator(
        conditions,
        'survey_rating_scale',
      );
      break;
    case 'link_events_any_specific_external_link':
      ruleItems.journeyValue = extractConditionValue(
        conditions,
        'action_journey_id',
      );
      ruleItems.touchpointValue = extractConditionValue(
        conditions,
        'reference_id',
      );
      ruleItems.linkValue = extractConditionValue(
        conditions,
        'sub_reference_id',
      );
      break;
    case 'survey_events_any_specific_survey':
      ruleItems.journeyValue = extractConditionValue(
        conditions,
        'reference_id',
      );
      ruleItems.touchpointValue = extractConditionValue(
        conditions,
        'sub_reference_id',
      );
      break;
    case 'email_events_any_specific_email':
      ruleItems.journeyValue = extractConditionValue(
        conditions,
        'reference_id',
      );
      ruleItems.touchpointValue = extractConditionValue(
        conditions,
        'sub_reference_id',
      );
      break;
  }
  if (has(ruleItems, 'criteriaValue')) {
    ruleItems.criteriaValue = criteriaValue;
    ruleItems.criteriaLabel =
      ABTAttributeKey &&
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[ABTAttributeKey].criteriaValues.find(
        // @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
        ({value}) => value === criteriaValue,
      )?.label;
  }
  if (has(ruleItems, 'fromDate')) {
    ruleItems.fromDate = extractConditionValue(conditions, 'start_date');
  }
  if (has(ruleItems, 'toDate')) {
    ruleItems.toDate = extractConditionValue(conditions, 'end_date');
  }
  // @ts-expect-error - TS2322 - Type '{ id: string; operator?: string | boolean | null | undefined; abtEntityType?: string; baseEntityType: string; abtAttributeName?: string; abtAttributeValue: string; criteriaLabel?: string | null | undefined; ... 41 more ...; relatedEntitiesArray: string[]; }' is not assignable to type 'AudienceRule'.
  return {
    id: uniqueId('rule'),
    ruleType: 'abt_rule',
    abtAttributeValue: ABTAttributeKey,
    entityType: conditions[0].field.entity_type,
    baseEntityType: conditions[0].field.base_entity_type,
    relatedEntitiesArray: getRelatedEntitiesForParsing(conditions),
    ...ruleItems,
  };
};

export const parseAudienceRule = (rule: AudienceRuleApi): AudienceRule => {
  return {
    id: uniqueId('filterRule'),
    //(diwakersurya) zipcode rules have separate operator on ui for radius
    operator: getClientOperator(rule),
    value: rule.value,
    field: rule.field,
    offset: rule.offset,
    meta: rule.metadata,
    include_null: rule.include_null === true ? true : false,

    // used for diffing
    changeType: rule?.changeType,
    description: rule?.description,
  };
};

export const getClientOperator = (rule: AudienceRuleApi): string | boolean => {
  if (rule.metadata?.source) {
    return 'in_radius';
  }
  if (rule.operator === 'eq' && typeof rule.value === 'boolean') {
    return rule.value;
  }
  if (rule.operator === 'eq' && rule.value === '') {
    return 'empty';
  }
  return rule.operator;
};

export const normalizeAudienceBlocks = (
  json: AudienceBlocks,
): AudienceBlocksApi => {
  const {inclusions, exclusions, all} = json;
  //if all is true, no exclusion, no inclusion
  if (all) {
    return {
      all,
      inclusions: null,
      exclusions: null,
    };
  }
  // @ts-expect-error - TS2869 - Right operand of ?? is unreachable because the left operand is never nullish.
  const inclusionBlocks = ([...inclusions.values()] ?? []).reduce<Array<any>>(
    (acc, rules) => {
      const conditions = rules.reduce<Array<any>>((innerAcc, rule) => {
        let normalizedRule;
        if (isNewActionTypeRule(rule)) {
          // @ts-expect-error - TS2345 - Argument of type 'AudienceRule' is not assignable to parameter of type 'NewActionTypeAudienceRule'.
          normalizedRule = normalizeNewActionRule(rule);
        }
        //NOTE(Vish): Handling DSL creation for action type rule differently.
        else if (isActionTypeRule(rule)) {
          normalizedRule = normalizeActionRule(rule);
        }
        //NOTE(Vish): Handling regular audience rule
        else {
          normalizedRule = normalizeAudienceRule(rule);
        }
        if (normalizedRule) {
          innerAcc.push(
            isActionTypeRule(rule)
              ? {conditions: normalizedRule}
              : {conditions: [normalizedRule]},
          );
        }
        return innerAcc;
      }, []);
      if (conditions.length > 0) {
        acc.push({rules: conditions});
      }
      return acc;
    },
    [],
  );

  // @ts-expect-error - TS2869 - Right operand of ?? is unreachable because the left operand is never nullish.
  const exclusionBlocks = ([...exclusions.values()] ?? []).reduce<Array<any>>(
    (acc, rules) => {
      //NOTE:(diwakersurya) for exclusions, each rule line becomes a block in backend instead of
      //single block with multiple rule lines because backend supports or only
      //at block level and exclusion rule lines require or between them

      rules.forEach((rule) => {
        let normalizedRule;
        if (isNewActionTypeRule(rule)) {
          // @ts-expect-error - TS2345 - Argument of type 'AudienceRule' is not assignable to parameter of type 'NewActionTypeAudienceRule'.
          normalizedRule = normalizeNewActionRule(rule);
        } else if (isActionTypeRule(rule)) {
          normalizedRule = normalizeActionRule(rule);
        } else {
          normalizedRule = normalizeAudienceRule(rule);
        }
        if (normalizedRule) {
          acc.push({
            rules: [
              {
                conditions: isActionTypeRule(rule)
                  ? normalizedRule
                  : [normalizedRule],
              },
            ],
          });
        }
      });
      return acc;
    },
    [],
  );
  return {
    all,
    inclusions: {blocks: inclusionBlocks},
    exclusions: {
      blocks: exclusionBlocks.length > 0 ? exclusionBlocks : [],
    },
  };
};

/*
Note(Vish): This function traverses the Entity Metadata Object and returns all the possible paths (in the form of parent keys)
to the provided entityType in a dfs manner. This behaviour might be modified later if the UX provides a way to zero in on the specific
entity path.
*/
function getRelationBasedOnEntity(
  // @ts-expect-error - TS7006 - Parameter 'entityObject' implicitly has an 'any' type.
  entityObject,
  entityType: string | null | undefined,
  acc: Array<Array<string>>,
  subAcc = [],
) {
  for (const subObj of Object.keys(entityObject)) {
    if (!isEmpty(entityObject[subObj].relatedEntities)) {
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
      subAcc.push(subObj);
      getRelationBasedOnEntity(
        entityObject[subObj].relatedEntities,
        entityType,
        acc,
        subAcc,
      );
    } else {
      if (entityObject[subObj].entityType === entityType) {
        acc.push([...subAcc, subObj]);
      }
    }
  }
  subAcc.splice(-1);
}

const getRelationForEntity = (
  baseEntityType: string | null | undefined,
  entityType: string | null | undefined,
  entityObject: EntityMetadataObject,
): Array<string> => {
  //TODO(vish): Write logic to derive this from entity metadata.
  const result: Array<Array<string>> = [];
  let relation: Array<string> = [];
  if (entityObject) {
    getRelationBasedOnEntity(entityObject.relatedEntities, entityType, result);
    //Note(Vish): This is a temp fix to get the smallest relation chain.
    const relations = result.sort((a, b) => a.length - b.length);
    relation = relations.length > 0 ? relations[0] : [];
  }
  return relation;
};

const normalizeActionRule = (
  rule: EntityTypeAudienceRule | ActionTypeAudienceRule,
) => {
  const DSL_STRUCTURE = {
    operator: 'eq',
    value: null,
    field: {
      attribute_name: null,
      base_entity_type:
        rule.baseEntityType != null ? rule.baseEntityType : null,
      // @ts-expect-error TS2551
      entity_type: rule.entityType != null ? rule.entityType : null,
      from_relations: [],
    },
    include_null: false,
  } as const;
  const rulesArray: Array<AudienceRuleApi> = [];
  const dslHelperArray = rule.abtAttributeValue
    ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[rule.abtAttributeValue].dslCreatorHelper
    : [];
  for (const helpObject of dslHelperArray) {
    const dslNode = cloneDeep(DSL_STRUCTURE);
    let shouldPushNode = true;
    for (const dslProp of Object.keys(helpObject)) {
      const [valueSource, valuePointer, objectLevel] = helpObject[dslProp];
      const value =
        // @ts-expect-error TS7053
        valueSource === 'fromRule' ? rule[valuePointer] : valuePointer;
      if (value == null) {
        shouldPushNode = false;
      }
      if (objectLevel != null) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: null; readonly base_entity_type: any; readonly entity_type: any; readonly from_relations: readonly []; }; readonly include_null: false; }'.
        dslNode[objectLevel][dslProp] = value;
      } else {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: null; readonly base_entity_type: any; readonly entity_type: any; readonly from_relations: readonly []; }; readonly include_null: false; }'.
        dslNode[dslProp] = value;
      }
    }
    if (shouldPushNode) {
      // @ts-expect-error - TS2345 - Argument of type '{ readonly operator: "eq"; readonly value: null; readonly field: { readonly attribute_name: null; readonly base_entity_type: any; readonly entity_type: any; readonly from_relations: readonly []; }; readonly include_null: false; }' is not assignable to parameter of type 'AudienceRuleApi'.
      rulesArray.push(dslNode);
    }
  }

  return rulesArray;
};

export const normalizeAudienceRule = (
  rule: AudienceRule,
): AudienceRuleApi | null | undefined => {
  // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
  if (rule.operator != null) {
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    let operator = rule.operator;
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'. | TS2339 - Property 'value' does not exist on type 'AudienceRule'.
    let value = rule.value != null && rule.value;
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'. | TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    if (rule.operator && VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator)) {
      value = null;
    }
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    if (rule.operator === 'in_radius') {
      operator = 'in';
    }
    if (
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      typeof rule.operator !== 'undefined' &&
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      [true, false].includes(rule.operator)
    ) {
      operator = 'eq';
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      value = rule.operator;
    }
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    if (rule.operator === 'empty') {
      operator = 'eq';
      value = '';
    }

    return {
      // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
      field: rule.field,
      value,
      operator,
      //TODO:(diwakersurya) fix me later
      metadata:
        // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
        rule.operator === 'in_radius'
          ? {
              // @ts-expect-error - TS2339 - Property 'meta' does not exist on type 'AudienceRule'.
              ...rule.meta,
              type: 'distance',
              operator: 'search',
            }
          : null,
      // @ts-expect-error - TS2339 - Property 'offset' does not exist on type 'AudienceRule'. | TS2339 - Property 'offset' does not exist on type 'AudienceRule'.
      offset: rule.offset != null ? rule.offset : null,
      // @ts-expect-error - TS2339 - Property 'include_null' does not exist on type 'AudienceRule'.
      include_null: rule.include_null === true ? true : false,
    };
  }
  return null;
};

export const DEFAULT_RENDER_CONFIG = {
  'createMode.header': 'Add',
  'review.confirm.button.label': 'Confirm and Publish',
  header: 'Create New List',
  'rules.intro.content': '',
  disableEntityTypeSelection: false,
  hideSaveAsDraft: false,
  showEditDetailsButtonOnReview: true,
  associatedResourceLabel: 'journey',
  canUseTalentPool: false,
} as const;

export const overrideRenderConfig = (
  newDisplayConfig: Partial<typeof DEFAULT_RENDER_CONFIG>,
): Partial<typeof DEFAULT_RENDER_CONFIG> => ({
  ...DEFAULT_RENDER_CONFIG,
  ...newDisplayConfig,
});

const getWorkflowTypeAssociatedResources = (
  associatedResources: AssociatedResources,
): AssociatedResources =>
  associatedResources.filter((resource) => resource.type === 'workflow');

export const getAssociatedJourneys = (
  associatedResources: AssociatedResources,
  additionalFilter?: (arg1: AssociatedResource) => boolean,
): AssociatedResources =>
  additionalFilter
    ? getWorkflowTypeAssociatedResources(associatedResources).filter(
        additionalFilter,
      )
    : getWorkflowTypeAssociatedResources(associatedResources);

export const getActiveAssociatedJourneys = (
  associatedResources: AssociatedResources,
  workflows: {
    [key: string]: Workflow;
  },
): AssociatedResources =>
  getAssociatedJourneys(associatedResources, ({id}) =>
    // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
    workflows[id] ? workflows[id].active : false,
  );

export const getInactiveAssociatedJourneys = (
  associatedResources: AssociatedResources,
  workflows: {
    [key: string]: Workflow;
  },
): AssociatedResources =>
  getAssociatedJourneys(associatedResources, ({id}) =>
    // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
    workflows[id] ? !workflows[id].active : true,
  );

const getAutomationWorkflowTypeAssociatedResources = (
  associatedResources: AssociatedResources,
): AssociatedResources =>
  associatedResources.filter(
    (resource) => resource.type === 'automation_workflow',
  );

export const getAssociatedWorkflows = (
  associatedResources: AssociatedResources,
  additionalFilter?: (arg1: AssociatedResource) => boolean,
): AssociatedResources =>
  additionalFilter
    ? getAutomationWorkflowTypeAssociatedResources(associatedResources).filter(
        additionalFilter,
      )
    : getAutomationWorkflowTypeAssociatedResources(associatedResources);

export const createRuleHash = (rule: AudienceRule): string => {
  //these fields determine uniqueness of a rule
  if (isActionTypeRule(rule)) {
    const hash = omit(rule, ['id', 'ruleType']);
    return hashObject(sortObject(hash));
  } else {
    // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'. | TS2339 - Property 'operator' does not exist on type 'AudienceRule'. | TS2339 - Property 'value' does not exist on type 'AudienceRule'.
    const {field, operator, value} = rule;
    return hashObject({field: sortObject(field), operator, value});
  }
};

export const createRulesHash = (rules: AudienceRule[]): string =>
  hashObject(
    rules.reduce<Record<string, any>>((acc, rule) => {
      acc[createRuleHash(rule)] = true;
      return acc;
    }, {}),
  );

export const getDefaultExclusionBlock = (): Map<string, AudienceRule[]> =>
  new Map([[createExclusionBlockIdentifier(), []]]);
export const getDefaultInclusionBlock = (): Map<string, AudienceRule[]> =>
  new Map([[createInclusionBlockIdentifier(), []]]);

export const mergeListRules = (
  lists: AudienceListApiResponse[],
): {
  all: boolean;
  inclusions: Map<string, AudienceRule[]>;
  exclusions: Map<string, AudienceRule[]>;
} => {
  const mergedRules = lists.reduce(
    (acc, list) => {
      // @ts-expect-error - TS2345 - Argument of type 'AudienceBlocksApi | undefined' is not assignable to parameter of type 'AudienceBlocksApi'.
      const {inclusions, exclusions, all} = parseAudienceBlocks(list.rule);
      if (all) {
        //if any list contains all true that means all true;
        acc.all = true;
      }
      for (const [_, value] of inclusions) {
        acc.inclusions.set(createInclusionBlockIdentifier(), value);
      }

      for (const [_, value] of exclusions) {
        if (value.length > 0) {
          acc.exclusions.set(createExclusionBlockIdentifier(), value);
        }
      }
      return acc;
    },
    {all: false, inclusions: new Map(), exclusions: new Map()},
  );
  if (mergedRules.all) {
    return {all: true, inclusions: new Map(), exclusions: new Map()};
  }
  if (mergedRules.exclusions.size === 0) {
    return {
      all: false,
      inclusions: mergedRules.inclusions,
      //empty exclusion block is shown on the ui even if
      //there is none.
      exclusions: getDefaultExclusionBlock(),
    };
  }

  return {
    all: false,
    inclusions: mergedRules.inclusions,
    //NOTE:(diwakersurya) all exclusion blocks are combined to form one exclusion block
    exclusions: new Map([
      [
        createExclusionBlockIdentifier(),
        [...mergedRules.exclusions.values()].flat(),
      ],
    ]),
  };
};

export const getAttributeIdentifierFromField = (rule: AudienceRule): string => {
  // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
  if (rule.field) {
    // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
    return rule.field.from_relations.length > 0
      ? // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'. | TS2339 - Property 'field' does not exist on type 'AudienceRule'.
        `${rule.field.from_relations.join('/')}/${rule.field.attribute_name}`
      : // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
        rule.field.attribute_name;
  }
  return '';
};

export const isRuleValueEmpty = (rule: AudienceRule): boolean => {
  return (
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    !!rule.operator &&
    // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
    ((!VALUE_NOT_REQUIRED_OPERATORS.includes(rule.operator) &&
      // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
      !rule.value &&
      // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
      rule.value !== 0) ||
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      (ARRAY_OPERATORS.includes(rule.operator) &&
        // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'. | TS2339 - Property 'value' does not exist on type 'AudienceRule'.
        !(Array.isArray(rule.value) && rule.value.length !== 0)))
  );
};

//NOTE:(diwakersurya) improvise later on if possible
export const validateAudienceBlocks = (
  json: AudienceBlocks,
  maxORNOTblocks?: number | null,
  fields?: DynamicLabels,
): {
  blockHashCount: Map<string, number>;
  blockwiseRuleHashCount: Map<string, Map<string, number>>;
  duplicateBlockExists: boolean;
  duplicateRuleExistsInAnyBlock: boolean;
  invalidRuleExistsInAnyBlock: boolean;
  noRuleExists: boolean;
  doBlocksExceedMaxAllowedBlocks: boolean;
} => {
  const {all, inclusions, exclusions} = json;
  const allBlocks: Map<string, AudienceRule[]> = new Map([
    ...inclusions,
    ...exclusions,
  ]);
  // if two blocks lead to same hash due to same value for all the rule line items
  const blockHashCount: Map<string, number> = [...allBlocks.values()].reduce(
    (acc, rules) => {
      if (rules.length > 0) {
        const hash = createRulesHash(rules);
        const val = acc.get(hash);
        if (val) {
          return acc.set(hash, val + 1);
        }
        acc.set(hash, 1);
      }
      return acc;
    },
    new Map(),
  );
  //duplicate rules will have the same hash. this is a map which stores
  // no of rules having the same hash to detect duplicates
  // map (block id,map(ruleHash,count))
  const blockwiseRuleHashCount = [...allBlocks.entries()].reduce(
    (outerAccumulator, [blockId, rules]: [any, any]) => {
      outerAccumulator.set(
        blockId,
        // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'rule' implicitly has an 'any' type.
        rules.reduce((acc, rule) => {
          const hash = createRuleHash(rule);
          const val = acc.get(hash);
          if (val) {
            return acc.set(hash, val + 1);
          }
          return acc.set(hash, 1);
        }, new Map()),
      );
      return outerAccumulator;
    },
    new Map(),
  );

  const duplicateBlockExists = [...blockHashCount.values()].some(
    (count) => count > 1,
  );
  const duplicateRuleExistsInAnyBlock = [
    ...blockwiseRuleHashCount.values(),
  ].some((ruleHashCount) =>
    [...ruleHashCount.values()].some((count) => count > 1),
  );

  const invalidRuleExistsInAnyBlock =
    all === false &&
    [...allBlocks.values()].some((rules) =>
      rules.some((rule) => {
        if (!isActionTypeRule(rule)) {
          // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
          if (typeof rule.operator === 'boolean') {
            return false;
          }
          // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
          if (!rule.operator) {
            return true;
          }
          if (isRuleValueEmpty(rule)) {
            return true;
          }
          const attributeIdentifier = getAttributeIdentifierFromField(rule);
          const isZipcodeField =
            // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'. | TS2339 - Property 'field' does not exist on type 'AudienceRule'.
            rule.field && rule.field.attribute_name && fields
              ? getIsZipcodeField(attributeIdentifier, fields)
              : false;
          if (
            isZipcodeField &&
            // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
            Array.isArray(rule.value) &&
            // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'.
            rule.value.length === 0
          ) {
            return true;
          }
          return false;
        }
        return isCriteriaBlockNotFilled(rule) || isScopeBlockNotFilled(rule);
      }),
    );

  const noRuleExists =
    all === false &&
    [...inclusions.values()].every((rules) => rules.length === 0);

  const doBlocksExceedMaxAllowedBlocks =
    maxORNOTblocks != null && all === false && inclusions.size > maxORNOTblocks;

  return {
    blockHashCount,
    blockwiseRuleHashCount,
    duplicateBlockExists,
    duplicateRuleExistsInAnyBlock,
    invalidRuleExistsInAnyBlock,
    noRuleExists,
    doBlocksExceedMaxAllowedBlocks,
  };
};

export const isAbtRule = (id: string): boolean => {
  return ABT_VALUES.has(id);
};

export const isActionTypeRule = (rule: AudienceRule): boolean => {
  // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
  if (rule.ruleType) {
    // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'. | TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
    return rule.ruleType === 'abt_rule' || rule.ruleType === 'abt_rule_new';
  }
  return false;
};

export const getCombinedRuleItems = (
  attributeValue: string,
): CombinedActionAudienceRule => {
  const initialRuleObject =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    ABT_ATTRIBUTE_KEY_SET[attributeValue].initialRuleItems;
  return {...initialRuleObject.criteriaField, ...initialRuleObject.scopeField};
};

export const getRuleItemsBasedOnAttributeValue = (
  attributeValue: string,
  entityType: string,
  baseEntityType: string,
  entityObject: EntityMetadataObject,
): ActionTypeAudienceRule => {
  const specificRuleItems = getCombinedRuleItems(attributeValue);
  const relatedEntitiesArray = getRelationForEntity(
    baseEntityType,
    entityType,
    entityObject,
  );
  // @ts-expect-error - TS2322 - Type '{ id: string; operator?: string | boolean | null | undefined; abtEntityType?: string; baseEntityType: string; abtAttributeName?: string; abtAttributeValue: string; criteriaLabel?: string | null | undefined; ... 41 more ...; relatedEntitiesArray: string[]; }' is not assignable to type 'ActionTypeAudienceRule'.
  return {
    id: uniqueId('rule'),
    ruleType: 'abt_rule',
    abtAttributeValue: attributeValue,
    entityType,
    baseEntityType,
    relatedEntitiesArray,
    ...specificRuleItems,
  };
};

//Needed in case of edit
export const deriveJourneyLabelFromValue = (
  journeyValue: string | null | undefined,
  workflows: {
    [key: string]: Workflow;
  },
): string => {
  if (journeyValue && workflows && workflows[journeyValue]) {
    return workflows[journeyValue].name;
  }
  return '';
};

//Needed in case of edit
export const deriveTouchpointLabelFromValue = (
  touchpointValue?: string | null,
  eventData?: {
    [eventId: string]: SenseApiEvent;
  } | null,
): string => {
  if (touchpointValue && eventData) {
    const touchpointObject = eventData[touchpointValue];
    if (touchpointObject) {
      return touchpointObject.title;
    }
    return '';
  }
  return '';
};

// @ts-expect-error - TS7023 - 'deriveModuleDataFromQuestion' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions. | TS7006 - Parameter 'modules' implicitly has an 'any' type.
const deriveModuleDataFromQuestion = (modules, question: string) => {
  if (modules && Array.isArray(modules) && modules.length > 0) {
    for (const module of modules) {
      if (module.id === question) {
        return module;
      }
      if (
        module.branches &&
        Array.isArray(module.branches) &&
        module.branches.length > 0
      ) {
        for (const branch of module.branches) {
          // @ts-expect-error - TS7022 - 'result' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
          const result = deriveModuleDataFromQuestion(branch.modules, question);
          if (result) {
            return result;
          }
        }
      }
    }
  }
};

export const deriveQuestionLabelFromValue = (
  eventData:
    | {
        [eventId: string]: SenseApiEvent;
      }
    | null
    | undefined,
  questionValue: string,
  touchpointValue: string,
): string => {
  if (eventData && eventData[touchpointValue]) {
    const moduleData = deriveModuleDataFromQuestion(
      eventData[touchpointValue].modules,
      questionValue,
    );
    if (moduleData?.question) {
      return moduleData.question;
    }
  }
  return '';
};

export const getCriteriaDisplayValue = (
  rule: AudienceRule,
  workflows: {
    [key: string]: Workflow;
  },
  eventData:
    | {
        [eventId: string]: SenseApiEvent;
      }
    | null
    | undefined,
  t: (arg1: string, arg2: string) => string,
): string => {
  //TODO(Vish): Complete this map for all abt attributes
  if (rule.abtAttributeValue) {
    switch (rule.abtAttributeValue) {
      case 'email_events_any_specific_email':
        // @ts-expect-error - TS2339 - Property 'touchpointLabel' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.touchpointLabel && rule.criteriaLabel) {
          // @ts-expect-error - TS2339 - Property 'touchpointLabel' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          return `${rule.touchpointLabel} ${rule.criteriaLabel}`;
        } else if (
          // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
          rule.journeyValue &&
          // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
          rule.touchpointValue &&
          // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          rule.criteriaLabel
        ) {
          const touchpointLabel = deriveTouchpointLabelFromValue(
            // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
            rule.touchpointValue,
            eventData,
          );
          // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          return `${touchpointLabel} ${rule.criteriaLabel ?? ''}`;
        } else {
          return t('Select_Criteria_and_Email', 'Select Criteria and Email');
        }
      case 'survey_events_any_specific_survey':
        // @ts-expect-error - TS2339 - Property 'touchpointLabel' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.touchpointLabel && rule.criteriaLabel) {
          // @ts-expect-error - TS2339 - Property 'touchpointLabel' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          return `${rule.touchpointLabel} ${rule.criteriaLabel}`;
        } else if (
          // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
          rule.journeyValue &&
          // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
          rule.touchpointValue &&
          // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          rule.criteriaLabel
        ) {
          const touchpointLabel = deriveTouchpointLabelFromValue(
            // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
            rule.touchpointValue,
            eventData,
          );
          // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          return `${touchpointLabel} ${rule.criteriaLabel ?? ''}`;
        } else {
          return t('Select_Criteria_and_Survey', 'Select Criteria and Survey');
        }
      case 'survey_events_any_specific_survey_question':
        // @ts-expect-error - TS2339 - Property 'questionValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.questionValue && rule.touchpointValue) {
          return (
            deriveQuestionLabelFromValue(
              eventData,
              // @ts-expect-error - TS2339 - Property 'questionValue' does not exist on type 'AudienceRule'.
              rule.questionValue,
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue,
            ) ?? t('Select_Survey_Question', 'Select Survey Question')
          );
        }
        return t('Select_Survey_Question', 'Select Survey Question');
      case 'link_events_any_specific_external_link':
        // @ts-expect-error - TS2339 - Property 'linkValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.linkValue && rule.criteriaLabel) {
          // @ts-expect-error - TS2339 - Property 'linkValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
          return `${rule.linkValue} ${rule.criteriaLabel}`; // Todo(Vish): Link value needs to be shortened to be displayed, refer figma (improvement)
        }
        return t('Select_Criteria_and_Link', 'Select Criteria and Link');
      default:
        return '';
    }
  }
  return '';
};

// this one being used in list v2 ABT rule edit flow
const getScopeDisplayValueForSurveySpecificQuestion = (
  rule: AudienceRule,
  t: (arg1: string, arg2: string) => string,
) => {
  if (
    // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
    rule.touchpointValue &&
    // @ts-expect-error - TS2339 - Property 'questionValue' does not exist on type 'AudienceRule'.
    rule.questionValue &&
    // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
    rule.fromDate &&
    // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
    rule.toDate
  ) {
    // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
    const fromDate = String(rule.fromDate);
    // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
    const toDate = String(rule.toDate);
    // @ts-expect-error - TS2339 - Property 'keywords' does not exist on type 'AudienceRule'. | TS2339 - Property 'keywords' does not exist on type 'AudienceRule'.
    if (rule.keywords && rule.keywords.length > 0) {
      return `${t('responded_with', 'responded with')} ${
        // @ts-expect-error - TS2339 - Property 'keywords' does not exist on type 'AudienceRule'. | TS2339 - Property 'keywords' does not exist on type 'AudienceRule'.
        rule.keywords && rule.keywords.length > 0
          ? // @ts-expect-error - TS2339 - Property 'keywords' does not exist on type 'AudienceRule'.
            rule.keywords.join(', ')
          : ''
      } ${t('from', 'from')} ${fromDate} ${t('to', 'to')} ${toDate}`;
    }
    // @ts-expect-error - TS2339 - Property 'npsValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'npsOperator' does not exist on type 'AudienceRule'.
    if (rule.npsValue && rule.npsOperator) {
      return `${t('responded_with', 'responded with')} ${String(
        // @ts-expect-error - TS2339 - Property 'npsOperator' does not exist on type 'AudienceRule'.
        getNpsOperatorLabel(rule.npsOperator ?? '', t),
        // @ts-expect-error - TS2339 - Property 'npsValue' does not exist on type 'AudienceRule'.
      )} ${String(rule.npsValue)} ${t('from', 'from')} ${fromDate} ${t(
        'to',
        'to',
      )} ${toDate}`;
    }
    // @ts-expect-error - TS2339 - Property 'multipleChoices' does not exist on type 'AudienceRule'. | TS2339 - Property 'multipleChoices' does not exist on type 'AudienceRule'.
    if (rule.multipleChoices && rule.multipleChoices.length > 0) {
      return `${t('responded_with_one_of', 'responded with one of')} ${
        // @ts-expect-error - TS2339 - Property 'multipleChoices' does not exist on type 'AudienceRule'. | TS2339 - Property 'multipleChoices' does not exist on type 'AudienceRule'.
        rule.multipleChoices && rule.multipleChoices.length > 0
          ? // @ts-expect-error - TS2339 - Property 'multipleChoices' does not exist on type 'AudienceRule'.
            rule.multipleChoices.join(', ')
          : ''
      } ${t('from', 'from')} ${fromDate} ${t('to', 'to')} ${toDate}`;
    }
    if (rule.calendarDateOperator) {
      return `${t('responded_date_is', 'responded date is')} ${String(
        getCalendarDateOperatorLabel(rule.calendarDateOperator ?? '', t),
      )} ${String(rule.calendarDateValue)}, ${t(
        'from',
        'from',
      )} ${fromDate} ${t('to', 'to')} ${toDate}`;
    }
    // @ts-expect-error - TS2339 - Property 'ratingScaleValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'ratingScaleOperator' does not exist on type 'AudienceRule'.
    if (rule.ratingScaleValue && rule.ratingScaleOperator) {
      return `${t('responded_with', 'responded with')} ${String(
        // @ts-expect-error - TS2339 - Property 'ratingScaleOperator' does not exist on type 'AudienceRule'.
        getNpsOperatorLabel(rule.ratingScaleOperator ?? '', t),
        // @ts-expect-error - TS2339 - Property 'ratingScaleValue' does not exist on type 'AudienceRule'.
      )} ${String(rule.ratingScaleValue)} ${t('from', 'from')} ${fromDate} ${t(
        'to',
        'to',
      )} ${toDate}`;
    }
  }
  return t('Select_Criteria_and_Scope', 'Select Criteria and Scope');
};

export const getScopeDisplayValue = (
  rule: AudienceRule,
  t: (arg1: string, arg2: string) => string,
): string => {
  // this function being used in list 2.0 abt
  const abtAttributeScopeMap = {
    email_events_any_specific_email:
      // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
      rule.fromDate && rule.toDate
        ? // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
          `${t('from', 'from')} ${rule.fromDate ?? ''} ${t('to', 'to')} ${
            // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            rule.toDate ?? ''
          }`
        : t('Select_Scope', 'Select Scope'),
    survey_events_any_specific_survey:
      // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
      rule.fromDate && rule.toDate
        ? // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
          `${t('from', 'from')} ${rule.fromDate ?? ''} ${t('to', 'to')} ${
            // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            rule.toDate ?? ''
          }`
        : t('Select_Scope', 'Select Scope'),
    survey_events_any_specific_survey_question:
      getScopeDisplayValueForSurveySpecificQuestion(rule, t),
    link_events_any_specific_external_link:
      // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
      rule.fromDate && rule.toDate
        ? // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
          `${t('from', 'from')} ${rule.fromDate ?? ''} ${t('to', 'to')} ${
            // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            rule.toDate ?? ''
          }`
        : t('Select_Scope', 'Select Scope'),
  } as const;
  if (rule.abtAttributeValue) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: string; readonly survey_events_any_specific_survey: string; readonly survey_events_any_specific_survey_question: string; readonly link_events_any_specific_external_link: string; }'.
    return abtAttributeScopeMap[rule.abtAttributeValue];
  }
  return '';
};

const getCriteriaFieldsToOmit = (abtAttributeValue: string) => {
  switch (abtAttributeValue) {
    case 'survey_events_any_specific_survey_question':
      return ['questionLabel', 'touchpointLabel', 'journeyLabel'];
    default:
      return ['journeyLabel', 'touchpointLabel'];
  }
};

// we are removing keys which are not required based on selected type of question
const getScopeFieldsToOmit = (
  abtAttributeValue: string,
  rule: EntityTypeAudienceRule | ActionTypeAudienceRule,
) => {
  const defaultFields = ['scopeLabel', 'scopeValue'];
  switch (abtAttributeValue) {
    case 'survey_events_any_specific_survey_question':
      // @ts-expect-error TS2339
      if (rule.multipleChoices && rule.multipleChoices.length > 0) {
        return [
          ...defaultFields,
          'npsOperator',
          'npsValue',
          'keywords',
          'calendarDateValue',
          'calendarDateOperator',
          'ratingScaleValue',
          'ratingScaleOperator',
        ];
      }
      // @ts-expect-error TS2339
      if (rule.keywords && rule.keywords.length > 0) {
        return [
          ...defaultFields,
          'npsOperator',
          'npsValue',
          'multipleChoices',
          'calendarDateValue',
          'calendarDateOperator',
          'ratingScaleValue',
          'ratingScaleOperator',
        ];
      }
      // @ts-expect-error TS2339
      if (rule.npsValue && rule.npsOperator) {
        return [
          ...defaultFields,
          'multipleChoices',
          'keywords',
          'calendarDateValue',
          'calendarDateOperator',
          'ratingScaleValue',
          'ratingScaleOperator',
        ];
      }
      if (rule.calendarDateOperator) {
        return [
          ...defaultFields,
          'npsOperator',
          'npsValue',
          'keywords',
          'multipleChoices',
          'ratingScaleValue',
          'ratingScaleOperator',
        ];
      }
      // @ts-expect-error TS2339
      if (rule.ratingScaleValue && rule.ratingScaleOperator) {
        return [
          ...defaultFields,
          'npsOperator',
          'npsValue',
          'keywords',
          'multipleChoices',
          'calendarDateValue',
          'calendarDateOperator',
        ];
      }
      return defaultFields;
    default:
      return defaultFields;
  }
};

export const isCriteriaBlockNotFilled = (rule: AudienceRule): boolean => {
  if (rule.abtAttributeValue) {
    const criteriaBlockItems =
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[rule.abtAttributeValue].initialRuleItems
        .criteriaField;
    const criteriaFieldsToOmit = getCriteriaFieldsToOmit(
      rule.abtAttributeValue,
    );
    const itemsToCheck = omit(criteriaBlockItems, criteriaFieldsToOmit);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AudienceRule'.
    return some(Object.keys(itemsToCheck), (item) => rule[item] == null);
  }
  return false;
};

export const isScopeBlockNotFilled = (rule: AudienceRule): boolean => {
  if (rule.abtAttributeValue) {
    const scopeBlockItems =
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[rule.abtAttributeValue].initialRuleItems.scopeField;
    const scopeFieldsToOmit = getScopeFieldsToOmit(
      rule.abtAttributeValue,
      rule,
    );
    const itemsToCheck = omit(scopeBlockItems, scopeFieldsToOmit);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'AudienceRule'.
    return some(Object.keys(itemsToCheck), (item) => rule[item] == null);
  }
  return false;
};

export const shouldShowMemberPreview = (status: string): boolean => {
  return ['live', 'published', 'draft', 'building'].includes(status);
};

const isABTRuleFromDSL = (conditions: Array<AudienceRuleApi>) => {
  let result = false;
  let AbtExhaustiveCriteriaList: Array<never> | Array<string> = [];
  for (const abtAttributeType of Object.keys(ABT_ATTRIBUTE_KEY_SET)) {
    const attributeCriteriaList =
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[abtAttributeType].criteriaList;
    AbtExhaustiveCriteriaList = [
      ...AbtExhaustiveCriteriaList,
      ...attributeCriteriaList,
    ];
  }
  for (const {field} of conditions) {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
    if (AbtExhaustiveCriteriaList.includes(field.attribute_name)) {
      result = true;
      break;
    }
  }
  return result;
};

export const cloneRuleWithoutValues = (
  rules: AudienceRule[],
): AudienceRule[] => {
  // @ts-expect-error - TS2322 - Type '({ id: string; operator?: string | boolean | null | undefined; abtEntityType?: string | undefined; baseEntityType: string | undefined; abtAttributeName?: string | undefined; abtAttributeValue: string; ... 46 more ...; offset?: undefined; } | { ...; } | { ...; } | { ...; })[]' is not assignable to type 'AudienceRule[]'.
  return rules.map((rule) => {
    if (isActionTypeRule(rule)) {
      // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
      if (rule.ruleType === 'abt_rule' && rule.abtAttributeValue) {
        const specificRuleItems = getCombinedRuleItems(rule.abtAttributeValue);
        return {
          id: uniqueId('rule'),
          ruleType: 'abt_rule',
          abtAttributeValue: rule.abtAttributeValue,
          // @ts-expect-error - TS2551 - Property 'entityType' does not exist on type 'AudienceRule'. Did you mean 'abtEntityType'?
          entityType: rule.entityType,
          baseEntityType: rule.baseEntityType,
          // @ts-expect-error - TS2339 - Property 'relatedEntitiesArray' does not exist on type 'AudienceRule'.
          relatedEntitiesArray: cloneDeep(rule.relatedEntitiesArray),
          ...specificRuleItems,
        };
        // @ts-expect-error - TS2339 - Property 'ruleType' does not exist on type 'AudienceRule'.
      } else if (rule.ruleType === 'abt_rule_new' && rule.abtAttributeName) {
        const {id, ...rest} = rule;
        return {
          id: uniqueId('rule'),
          ...rest,
        };
      }
    }
    return {
      id: uniqueId('filterRule'),
      // @ts-expect-error - TS2339 - Property 'field' does not exist on type 'AudienceRule'.
      field: cloneDeep(rule.field),
      // @ts-expect-error - TS2339 - Property 'operator' does not exist on type 'AudienceRule'. | TS2339 - Property 'operator' does not exist on type 'AudienceRule'.
      operator: rule.operator && rule.operator,
      include_null: null,
      meta: null,
      offset: null,
    };
  });
};

export const getQuestionType = (
  touchpointValue?: string | null,
  questionValue?: string | null,
  eventData?: {
    [eventId: string]: SenseApiEvent;
  } | null,
): string => {
  if (touchpointValue && questionValue) {
    const moduleData = deriveModuleDataFromQuestion(
      eventData?.[touchpointValue].modules,
      questionValue,
    );
    if (moduleData?.module_type) {
      return moduleData.module_type;
    }
  }
  return '';
};

export const getAllQuestions = (
  touchpointValue?: string | null,
  questionValue?: string | null,
  eventData?: {
    [eventId: string]: SenseApiEvent;
  } | null,
): string[] => {
  if (
    touchpointValue &&
    questionValue &&
    eventData &&
    !isEmpty(eventData) &&
    !isEmpty(eventData[touchpointValue]) &&
    Array.isArray(eventData[touchpointValue].modules)
  ) {
    const moduleData = deriveModuleDataFromQuestion(
      eventData[touchpointValue].modules,
      questionValue,
    );
    if (moduleData && moduleData.choices) {
      return moduleData.choices;
    }
    return [];
  }
  return [];
};

export const getNpsOperatorOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
}> => [
  {value: 'eq', label: t('Equal_to', 'Equal to')},
  {value: 'gt', label: t('Greater_than', 'Greater than')},
  {value: 'lt', label: t('Less_than', 'Less than')},
  {
    value: 'gte',
    label: t('Greater_than_or_equal_to', 'Greater than or equal to'),
  },
  {value: 'lte', label: t('Less_than_or_equal_to', 'Less than or equal to')},
];

export const getNpsOperatorLabel = (
  operator: string,
  t: (arg1: string, arg2: string) => string,
): string | null | undefined =>
  getNpsOperatorOptions(t).find(({value}) => value === operator)?.label;

export const getNpsValues = (): {
  label: string;
  value: string;
}[] => {
  const valueArray: Array<{
    label: string;
    value: string;
  }> = [];
  for (let i = 0; i <= 10; i++) {
    valueArray.push({label: String(i), value: String(i)});
  }
  return valueArray;
};

export const getRatingScaleValues = (): {
  label: string;
  value: string;
}[] => {
  const valueArray: Array<{
    label: string;
    value: string;
  }> = [];
  for (let i = 1; i <= 10; i++) {
    valueArray.push({label: String(i), value: String(i)});
  }
  return valueArray;
};

export const getCalendarDateOperatorOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  value: string;
  label: string;
}> => [
  {
    value: 'gt',
    label: t('After', 'After'),
  },
  {
    value: 'lt',
    label: t('Before', 'Before'),
  },
  {
    value: 'eq',
    label: t('Equal_to', 'Equal to'),
  },
  {
    value: 'gte',
    label: t('On_or_after', 'On or after'),
  },
  {
    value: 'lte',
    label: t('On_or_before', 'On or before'),
  },
];

export const getCalendarDateOperatorLabel = (
  operator: string,
  t: (arg1: string, arg2: string) => string,
): string | null | undefined =>
  getCalendarDateOperatorOptions(t).find(({value}) => value === operator)
    ?.label;

export const getJourneyIdForFetchingEvents = (
  journeyValue?: string | null,
): string | null | undefined => {
  if (journeyValue != null) {
    return journeyValue;
  }
  return null;
};

export const getSupportedActionEntityAttributes = (
  actionEntityAttributes: EntityMetadataEntityAttributes[] | null | undefined,
  abtAttributeType: string,
): {
  label: string;
  value: string;
}[] =>
  actionEntityAttributes && !isEmpty(actionEntityAttributes)
    ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[abtAttributeType]['criteriaValues'].filter(
        // @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
        ({value}) =>
          actionEntityAttributes.some(
            (attribute) => attribute.attributeName === value,
          ),
      )
    : [];

const surveyTokens = [surveyContentToken, surveyLinkEmailToken];

export const eventContainsSurveyBlock = (
  event: SenseApiEvent,
  tokens: string[] = surveyTokens,
): boolean => {
  if (event.beefree_json != null) {
    const doc =
      typeof event.beefree_json === 'object'
        ? JSON.stringify(event.beefree_json)
        : event.beefree_json;
    return (
      tokens.reduce((sum, token) => checkForString(doc, token) + sum, 0) > 0
    );
  }
  return false;
};

export const doesTouchpointBelongToThisAction = (
  abtAttributeValue: string,
  sendType?: string | null,
  eventType?: string | null,
  event?: SenseApiEvent | null,
): boolean => {
  const supported_send_types =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    ABT_ATTRIBUTE_KEY_SET[abtAttributeValue].supportedTouchpointTypes.send_type;
  const supported_event_types =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    ABT_ATTRIBUTE_KEY_SET[abtAttributeValue].supportedTouchpointTypes
      .event_type;
  //NOTE(Vish) : Handling the case when survey is not present in beefree touchpoint
  if (
    event &&
    (abtAttributeValue === 'survey_events_any_specific_survey_question' ||
      abtAttributeValue === 'survey_events_any_specific_survey') &&
    eventType === 'beefree_email'
  ) {
    return eventContainsSurveyBlock(event);
  }
  return (
    // @ts-expect-error - TS7006 - Parameter 'type' implicitly has an 'any' type.
    supported_send_types.some((type) => type === sendType) ||
    // @ts-expect-error - TS7006 - Parameter 'type' implicitly has an 'any' type.
    supported_event_types.some((type) => type === eventType)
  );
};

export const getAssociatedTouchpoints = (
  abtAttributeValue: string,
  workflowData?: ApiWorkflow | null,
): Array<{
  label: string;
  value: string;
}> => {
  let result:
    | undefined
    | Array<{
        label: string;
        value: string;
      }> = [];
  if (workflowData && !isEmpty(workflowData)) {
    result = workflowData.events_summary?.reduce(
      (
        acc: Array<{
          label: string;
          value: string;
        }>,
        touchpoint: {
          event_type: string;
          id: string;
          send_type: string;
          title: string;
        },
      ) => {
        if (
          doesTouchpointBelongToThisAction(
            abtAttributeValue,
            touchpoint.send_type,
            touchpoint.event_type,
            workflowData.events[touchpoint.id],
          )
        ) {
          return acc.concat({
            label: touchpoint.title,
            value: touchpoint.id,
          });
        }
        return acc;
      },
      [],
    );
  }
  return result || [];
};

const getAssociatedQuestionsHelper = (
  // @ts-expect-error - TS7006 - Parameter 'modules' implicitly has an 'any' type.
  modules,
  acc: Array<{
    label: string;
    value: string;
  }>,
) => {
  if (modules && Array.isArray(modules) && modules.length > 0) {
    for (const module of modules) {
      if (ABT_SUPPORTED_SURVEY_QUESTIONS.includes(module.module_type)) {
        const question = {
          label: module.question ? module.question : '',
          value: module.id ? module.id : '',
        } as const;
        acc.push(question);
      }
      if (
        module.branches &&
        Array.isArray(module.branches) &&
        module.branches.length > 0
      ) {
        for (const branch of module.branches) {
          getAssociatedQuestionsHelper(branch.modules, acc);
        }
      }
    }
  }
};

export const getAssociatedQuestions = (
  selectedTouchpointId?: string | null,
  eventData?: {
    [eventId: string]: SenseApiEvent;
  } | null,
): Array<{
  label: string;
  value: string;
}> => {
  if (!selectedTouchpointId) {
    return [];
  }
  if (
    eventData &&
    !isEmpty(eventData) &&
    !isEmpty(eventData[selectedTouchpointId])
  ) {
    const questions: Array<{
      label: string;
      value: string;
    }> = [];
    getAssociatedQuestionsHelper(
      eventData[selectedTouchpointId].modules,
      questions,
    );
    return questions;
  }
  return [];
};

export const checkCriteriaValidity = (rule: AudienceRule): boolean => {
  if (rule.abtAttributeValue) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    return ABT_ATTRIBUTE_KEY_SET[
      rule.abtAttributeValue
      // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type. | TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'AudienceRule'.
    ].requiredCriteriaKeys.every((key) => rule[key]);
  }
  return false;
};

export const checkScopeValidity = (rule: AudienceRule): boolean => {
  if (rule.abtAttributeValue) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
    return ABT_ATTRIBUTE_KEY_SET[
      rule.abtAttributeValue
      // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type. | TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'AudienceRule'.
    ].requiredScopeKeys.every((key) => rule[key]);
  }
  return false;
};

// @ts-expect-error - TS2345 - Argument of type 'Date' is not assignable to parameter of type 'number'.
const isValidDateObject = (d: Date) => d instanceof Date && !isNaN(d);

export const isABTDateSelectionValid = (
  min: string | null,
  max: string | null,
  fromDate: string,
  toDate: string,
  scopeValue: string,
): boolean => {
  const isValidFromDate = isValidDateObject(new Date(fromDate));
  let isValidToDate = false;
  if (scopeValue === 'specific_date_to_now' && toDate === 'now') {
    isValidToDate = true;
  } else {
    isValidToDate = isValidDateObject(new Date(toDate));
  }
  if (!isValidFromDate || !isValidToDate) {
    return false;
  }

  const from = new Date(fromDate);

  if (scopeValue === 'specific_date_to_now' && toDate === 'now') {
    const isMinDateValid = min == null || from >= new Date(min);
    const isMaxDateValid = max == null || from <= new Date(max);

    return isMinDateValid && isMaxDateValid;
  }

  const to = new Date(toDate);
  const isMinDateValid =
    min == null || (from >= new Date(min) && to >= new Date(min));
  const isMaxDateValid =
    max == null || (from <= new Date(max) && to <= new Date(max));

  return isMinDateValid && isMaxDateValid;
};

const getActionNameFromDSL = (conditions: Array<AudienceRuleApi>) => {
  const actionAttributeTypeToNameMapping = {
    email_events_any_specific_email: 'Email Action',
    survey_events_any_specific_survey: 'Survey Action',
    survey_events_any_specific_survey_question: 'Survey Question Action',
    link_events_any_specific_external_link: 'External Link Action',
  } as const;
  for (const abtAttributeType of Object.keys(ABT_ATTRIBUTE_KEY_SET)) {
    const attributeCriteriaList =
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: { readonly initialRuleItems: { readonly criteriaField: { readonly criteriaLabel: null; readonly criteriaValue: null; readonly journeyLabel: null; readonly journeyValue: null; readonly touchpointLabel: null; readonly touchpointValue: null; }; readonly scopeField: { ...; }; ...'.
      ABT_ATTRIBUTE_KEY_SET[abtAttributeType].criteriaList;
    for (const {field} of conditions) {
      if (attributeCriteriaList.includes(field.attribute_name)) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_events_any_specific_email: "Email Action"; readonly survey_events_any_specific_survey: "Survey Action"; readonly survey_events_any_specific_survey_question: "Survey Question Action"; readonly link_events_any_specific_external_link: "External Link Action"; }'.
        return actionAttributeTypeToNameMapping[abtAttributeType];
      }
    }
  }
  return null;
};
export const getTrackingProps = (
  rule: AudienceBlocksApi,
): {
  number_of_include_blocks: number;
  is_not_block_present: boolean;
  is_action_rule_present_in_list: boolean;
  list_of_actions_used_in_include_section: Array<string | null | undefined>;
  list_of_actions_used_in_exclude_section: Array<string | null | undefined>;
} => {
  const inclusionBlocks = rule.inclusions?.blocks;
  const exclusionBlock = rule.exclusions?.blocks;
  const inclusionBlocksLength = Array.isArray(inclusionBlocks)
    ? inclusionBlocks.length
    : 0;
  const exclusionBlocksLength = Array.isArray(exclusionBlock)
    ? exclusionBlock.length
    : 0;
  let isActionRuleUsed = false;
  const ActionListItemsIncludeSection: Array<string | null | undefined> = [];
  const ActionListItemsExcludeSection: Array<string | null | undefined> = [];
  if (inclusionBlocks && inclusionBlocksLength > 0) {
    for (const block of inclusionBlocks) {
      for (const innerRule of block.rules) {
        if (isABTRuleFromDSL(innerRule.conditions)) {
          isActionRuleUsed = true;
          ActionListItemsIncludeSection.push(
            getActionNameFromDSL(innerRule.conditions),
          );
        }
      }
    }
  }
  if (exclusionBlock && exclusionBlocksLength > 0) {
    for (const innerRule of exclusionBlock[0].rules) {
      if (isABTRuleFromDSL(innerRule.conditions)) {
        isActionRuleUsed = true;
        ActionListItemsExcludeSection.push(
          getActionNameFromDSL(innerRule.conditions),
        );
      }
    }
  }
  return {
    number_of_include_blocks: inclusionBlocksLength,
    is_not_block_present: exclusionBlocksLength > 0,
    is_action_rule_present_in_list: isActionRuleUsed,
    list_of_actions_used_in_include_section: ActionListItemsIncludeSection,
    list_of_actions_used_in_exclude_section: ActionListItemsExcludeSection,
  };
};

const getScopeDisplayValueForSurveySpecificQuestionGen = (
  rule: ActionTypeAudienceRule,
  t: (arg1: string, arg2: string) => string,
) => {
  if (
    rule.touchpointValue &&
    rule.questionValue &&
    rule.fromDate &&
    rule.toDate
  ) {
    const fromDate = String(rule.fromDate);
    const toDate = String(rule.toDate);
    if (rule.keywords && rule.keywords.length > 0) {
      return `${t('responded_with', 'responded with')} ${
        rule.keywords && rule.keywords.length > 0
          ? rule.keywords.join(', ')
          : ''
      }`;
    }
    if (rule.npsValue && rule.npsOperator) {
      return `${t('responded_with', 'responded with')} ${String(
        getNpsOperatorLabel(rule.npsOperator ?? '', t),
      )} ${String(rule.npsValue)}`;
    }
    if (rule.multipleChoices && rule.multipleChoices.length > 0) {
      return `${t('responded_with_one_of', 'responded with one of')} ${
        rule.multipleChoices && rule.multipleChoices.length > 0
          ? rule.multipleChoices.join(', ')
          : ''
      }`;
    }
    if (rule.calendarDateOperator) {
      return `${t('responded_date_is', 'responded date is')} ${String(
        getCalendarDateOperatorLabel(rule.calendarDateOperator ?? '', t),
      )} ${String(rule.calendarDateValue)}, ${t(
        'from',
        'from',
      )} ${fromDate} ${t('to', 'to')} ${toDate}`;
    }
    if (rule.ratingScaleValue && rule.ratingScaleOperator) {
      return `${t('responded_with', 'responded with')} ${String(
        getNpsOperatorLabel(rule.ratingScaleOperator ?? '', t),
      )} ${String(rule.ratingScaleValue)} ${t('from', 'from')} ${fromDate} ${t(
        'to',
        'to',
      )} ${toDate}`;
    }
  }
  return '';
};

export const getAbtDisplayValues = (
  rule: AudienceRule,
  workflows: {
    [key: string]: Workflow;
  },
  eventData:
    | {
        [eventId: string]: SenseApiEvent;
      }
    | null
    | undefined,
  t: (arg1: string, arg2: string) => string,
): string[] => {
  //TODO(Vish): Complete this map for all abt attributes
  if (rule.abtAttributeValue) {
    const result: Array<string> = [];
    switch (rule.abtAttributeValue) {
      case 'email_events_any_specific_email':
        // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
        if (rule.journeyValue) {
          result.push(
            `${t('Journey', 'Journey')} - ${deriveJourneyLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
              rule.journeyValue,
              workflows,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.touchpointValue) {
          result.push(
            `${t(
              'Touchpoint',
              'Touchpoint',
            )} - ${deriveTouchpointLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue,
              eventData,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.criteriaLabel) {
          result.push(
            // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
            `${t('Criteria', 'Criteria')} - ${rule.criteriaLabel ?? ''}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
        if (rule.fromDate && rule.toDate) {
          result.push(
            `${t('Scope', 'Scope')} - ${t('from', 'from')} ${
              // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
              rule.fromDate ?? ''
              // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            } ${t('to', 'to')} ${rule.toDate ?? ''}`,
          );
        }
        return result;
      case 'survey_events_any_specific_survey':
        // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
        if (rule.journeyValue) {
          result.push(
            `${t('Journey', 'Journey')} - ${deriveJourneyLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
              rule.journeyValue,
              workflows,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.touchpointValue) {
          result.push(
            `${t(
              'Touchpoint',
              'Touchpoint',
            )} - ${deriveTouchpointLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue,
              eventData,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.criteriaLabel) {
          result.push(
            // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
            `${t('Criteria', 'Criteria')} - ${rule.criteriaLabel ?? ''}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
        if (rule.fromDate && rule.toDate) {
          result.push(
            `${t('Scope', 'Scope')} - ${t('from', 'from')} ${
              // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
              rule.fromDate ?? ''
              // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            } ${t('to', 'to')} ${rule.toDate ?? ''}`,
          );
        }
        return result;
      case 'survey_events_any_specific_survey_question':
        // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
        if (rule.journeyValue) {
          result.push(
            `${t('Journey', 'Journey')} - ${deriveJourneyLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
              rule.journeyValue,
              workflows,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.touchpointValue) {
          result.push(
            `${t(
              'Touchpoint',
              'Touchpoint',
            )} - ${deriveTouchpointLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue,
              eventData,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'questionValue' does not exist on type 'AudienceRule'. | TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.questionValue && rule.touchpointValue) {
          result.push(
            `${t('Question', 'Question')} - ${deriveQuestionLabelFromValue(
              eventData,
              // @ts-expect-error - TS2339 - Property 'questionValue' does not exist on type 'AudienceRule'.
              rule.questionValue ?? '',
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue ?? '',
            )}`,
          );
          result.push(
            `${t(
              'Criteria',
              'Criteria',
              // @ts-expect-error - TS2345 - Argument of type 'AudienceRule' is not assignable to parameter of type 'ActionTypeAudienceRule'.
            )} - ${getScopeDisplayValueForSurveySpecificQuestionGen(rule, t)}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
        if (rule.fromDate && rule.toDate) {
          result.push(
            `${t('Scope', 'Scope')} - ${t('from', 'from')} ${
              // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
              rule.fromDate ?? ''
              // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            } ${t('to', 'to')} ${rule.toDate ?? ''}`,
          );
        }
        return result;
      case 'link_events_any_specific_external_link':
        // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
        if (rule.journeyValue) {
          result.push(
            `${t('Journey', 'Journey')} - ${deriveJourneyLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'journeyValue' does not exist on type 'AudienceRule'.
              rule.journeyValue,
              workflows,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
        if (rule.touchpointValue) {
          result.push(
            `${t(
              'Touchpoint',
              'Touchpoint',
            )} - ${deriveTouchpointLabelFromValue(
              // @ts-expect-error - TS2339 - Property 'touchpointValue' does not exist on type 'AudienceRule'.
              rule.touchpointValue,
              eventData,
            )}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'linkValue' does not exist on type 'AudienceRule'.
        if (rule.linkValue) {
          // @ts-expect-error - TS2339 - Property 'linkValue' does not exist on type 'AudienceRule'.
          result.push(`${t('Link', 'Link')} - ${rule.linkValue ?? ''}`);
        }
        // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
        if (rule.criteriaLabel) {
          result.push(
            // @ts-expect-error - TS2339 - Property 'criteriaLabel' does not exist on type 'AudienceRule'.
            `${t('Criteria', 'Criteria')} - ${rule.criteriaLabel ?? ''}`,
          );
        }
        // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'. | TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
        if (rule.fromDate && rule.toDate) {
          result.push(
            `${t('Scope', 'Scope')} - ${t('from', 'from')} ${
              // @ts-expect-error - TS2339 - Property 'fromDate' does not exist on type 'AudienceRule'.
              rule.fromDate ?? ''
              // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type 'AudienceRule'.
            } ${t('to', 'to')} ${rule.toDate ?? ''}`,
          );
        }
        return result;
      default:
        return [];
    }
  }
  return [];
};

export const doesValueExistInAnyRule = (rules: AudienceRule[]): boolean => {
  return rules.some((rule) => {
    if (isActionTypeRule(rule)) {
      /*Note(Vish): Rest all ABT events for now contain criteria as the defining value except survey question.
        In future if more ABT events are added this function should be modified accordingly
      */
      if (
        rule.abtAttributeValue === 'survey_events_any_specific_survey_question'
      ) {
        return has(rule, 'questionValue') && rule.questionValue != null;
      }
      return has(rule, 'criteriaValue') && rule.criteriaValue != null;
    }
    return has(rule, 'value') && rule.value != null;
  });
};

export const mergeListRulesAndGetListsMap = (
  lists: AudienceListApiResponse[],
): {
  audienceBlocks: {
    all: boolean;
    inclusions: Map<string, AudienceRule[]>;
    exclusions: Map<string, AudienceRule[]>;
  };
  listRuleIdMap: Map<string, string[]>;
} => {
  const listRuleIdMap = new Map<string, string[]>();
  const mergedRules = lists.reduce(
    (acc, list) => {
      // @ts-expect-error - TS2345 - Argument of type 'AudienceBlocksApi | undefined' is not assignable to parameter of type 'AudienceBlocksApi'.
      const {inclusions, exclusions, all} = parseAudienceBlocks(list.rule);
      const ruleKeys: Array<string> = [];
      for (const block of inclusions.values()) {
        block.forEach(({id}) => ruleKeys.push(id));
      }
      for (const block of exclusions.values()) {
        block.forEach(({id}) => ruleKeys.push(id));
      }
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      listRuleIdMap.set(list.id, ruleKeys);
      if (all) {
        //if any list contains all true that means all true;
        acc.all = true;
      }
      for (const [_, value] of inclusions) {
        acc.inclusions.set(createInclusionBlockIdentifier(), value);
      }

      for (const [_, value] of exclusions) {
        if (value.length > 0) {
          acc.exclusions.set(createExclusionBlockIdentifier(), value);
        }
      }
      return acc;
    },
    {all: false, inclusions: new Map(), exclusions: new Map()},
  );
  if (mergedRules.all) {
    return {
      audienceBlocks: {
        all: true,
        //empty inclusion and exclusion block is shown on the ui for include all
        inclusions: getDefaultInclusionBlock(),
        exclusions: getDefaultExclusionBlock(),
      },
      listRuleIdMap,
    };
  }
  if (mergedRules.exclusions.size === 0) {
    return {
      audienceBlocks: {
        all: false,
        inclusions: mergedRules.inclusions,
        //empty exclusion block is shown on the ui even if
        //there is none.
        exclusions: getDefaultExclusionBlock(),
      },
      //Note(Vish): ListRuleIdMap is required in genesis list builder to highlight corresponding rules for a list in the copy lists screen
      listRuleIdMap,
    };
  }

  return {
    audienceBlocks: {
      all: false,
      inclusions: mergedRules.inclusions,
      //NOTE:(diwakersurya) all exclusion blocks are combined to form one exclusion block
      exclusions: new Map([
        [
          createExclusionBlockIdentifier(),
          [...mergedRules.exclusions.values()].flat(),
        ],
      ]),
    },
    listRuleIdMap,
  };
};
export function placeholderValue(
  val?: string | number | boolean | null,
  placeholder: string = '--',
): string {
  if (val == null) {
    return placeholder;
  }
  return val.toString();
}

export const ruleContextMenuActions = (
  cloneBlock: () => unknown,
  removeBlock: () => unknown,
  rules: AudienceRule[],
  t: (arg1: string, arg2: string) => string,
): {
  label: string;
  key: string;
  iconLeft: string;
}[] => {
  const options: Array<{
    iconLeft: string;
    key: string;
    label: string;
  }> = [];
  if (typeof cloneBlock === 'function') {
    options.push({
      label: t('Clone_block', 'Clone block'),
      key: 'clone',
      iconLeft: 'clone',
    });
  }
  if (doesValueExistInAnyRule(rules)) {
    options.push({
      label: t('Clear_all_values', 'Clear all values'),
      key: 'clear',
      iconLeft: 'eraser',
    });
  }

  if (typeof removeBlock === 'function') {
    options.push({
      label: t('Delete_block', 'Delete block'),
      key: 'delete',
      iconLeft: 'trash-can',
    });
  }
  return options;
};

export const getRelativeDateDisplayBasedOnOffset = (
  offset: number,
  t: (arg1: string, arg2: string) => string,
): string => {
  const absoluteOffset = Math.abs(offset);
  return offset > 0
    ? `${absoluteOffset} ${t('days_from_now', 'days from now')}`
    : `${absoluteOffset} ${t('days_ago', 'days ago')}`;
};

export const checkDateString = (
  dataType: string,
  value: string | number,
): boolean => {
  if (dataType === 'date') {
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return !isNaN(date.getTime());
    }
  }
  return false;
};

export const getRuleDisplayValue = (
  rule: AudienceRule,
  noValueText: string,
  dataType: string,
  t: (arg1: string, arg2: string) => string,
  type?: string,
): string | string[] => {
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'AudienceRule'. | TS2339 - Property 'value' does not exist on type 'AudienceRule'. | TS2339 - Property 'value' does not exist on type 'AudienceRule'.
  const value = rule.value || rule.value === 0 ? rule.value : null;
  // @ts-expect-error - TS2339 - Property 'offset' does not exist on type 'AudienceRule'. | TS2339 - Property 'offset' does not exist on type 'AudienceRule'.
  const offset = rule.offset ? rule.offset : 0;
  if (Array.isArray(value)) {
    if (type === 'main') {
      const valueCsv = value.length ? value.join(', ') : noValueText;
      /**
       * The text is being displayed inside a button where only 50-90 characters are visible;
       * the rest are truncated. However, large strings sometimes prevent truncation.
       * Slicing the string will make the DOM lighter.
       **/
      return valueCsv.slice(0, 100);
    }
    return value.length > 0 ? value.map(String) : noValueText;
  }
  if (value || value === 0) {
    if (dataType === 'date' && offset) {
      return getRelativeDateDisplayBasedOnOffset(offset, t);
    }
    return String(value);
  }
  return noValueText;
};

export const isLocalValueEmpty = (value: string | string[]): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return !value;
};

export const isRuleDisabled = (
  highlightText: string,
  value: string | string[],
): boolean => {
  if (highlightText.length > 0) {
    if (Array.isArray(value)) {
      return !value.some((val) =>
        val.toLowerCase().includes(highlightText.toLowerCase()),
      );
    }
    return !value.toLowerCase().includes(highlightText.toLowerCase());
  }
  return false;
};

export const isAllowEmptySupported = (
  dataType: string,
  operator: string,
): boolean => {
  if (
    ['string', 'email', 'phone'].includes(dataType) &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"in"'.
    ALLOWS_EMPTY_STRING_OPERATORS.includes(operator)
  ) {
    return true;
  } else if (
    ['date'].includes(dataType) &&
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"eq" | "gt" | "lt" | "gte" | "lte"'.
    ALLOWS_EMPTY_DATE_OPERATORS.includes(operator)
  ) {
    return true;
  }
  return false;
};

export const getPaginatedEntries = (
  entries: VariableEntry[],
  pagination: PaginationData,
): VariableEntry[] => {
  return [...entries].slice(
    (pagination.currentPage - 1) * pagination.pageSize,
    pagination.currentPage * pagination.pageSize,
  );
};

export const getSortedEntries = (
  entries: VariableEntry[],
  sort: SortData,
): VariableEntry[] => {
  const sortingDirection =
    sort.sortOrder === 'asc' ? 1 : sort.sortOrder === 'desc' ? -1 : 0;
  return [...entries].sort((firstEntry, secondEntry) => {
    const firstEntrySortByValue = get(firstEntry, sort.sortBy);
    const secondEntrySortByValue = get(secondEntry, sort.sortBy);
    return (
      firstEntrySortByValue.localeCompare(secondEntrySortByValue) *
      sortingDirection
    );
  });
};

export const getAssociatedResourceLabel = (
  config: Partial<{
    [key: string]: string | boolean | React.ReactNode;
  }>,
): string | null | undefined => {
  const {associatedResourceLabel} = config;
  if (typeof associatedResourceLabel === 'string') {
    return associatedResourceLabel;
  }
  return null; // or handle it in a way that suits your needs
};

export const getFocusEntityPluralValue = (
  focusEntity: string,
  t: (arg1: string, arg2: string) => string,
  makeKey: (arg1: string) => string,
): string => {
  const focusEntityPluralMap: Record<string, string> = {
    'Internal employee': t('Internal_employees', 'Internal employees'),
    Candidate: t('Candidates', 'Candidates'),
    'Job order': t('Job_orders', 'Job orders'),
    Contact: t('Contacts', 'Contacts'),
    Submittal: t('Submittals', 'Submittals'),
    Placement: t('Placements', 'Placements'),
    Interview: t('Interviews', 'Interviews'),
    Company: t('Companies', 'Companies'),
    'Candidate reference': t('Candidate_references', 'Candidate references'),
    'Custom person': t('Custom_people', 'Custom people'),
    Certification: t('Certifications', 'Certifications'),
    Lead: t('Leads', 'Leads'),
    Opportunity: t('Opportunities', 'Opportunities'),
    'Candidate lead': t('Candidate_leads', 'Candidate leads'),
    'Candidate match': t('Candidate_matches', 'Candidate matches'),
    'Contact lead': t('Contact_leads', 'Contact leads'),
    'Talent Pool status': t('Talent_Pool_statuses', 'Talent Pool statuses'),
  };

  const focusEntityPlural =
    focusEntityPluralMap[focusEntity] ||
    t(makeKey(focusEntity + 's'), focusEntity + 's');
  return focusEntityPlural;
};

export const getQuerySourceFromListType = (listType: string | null) => {
  if (listType === 'atsList') {
    return 'ATS_LIST';
  } else {
    return 'LIST_UI,CSV_LIST_UI';
  }
};
